import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import {
  ArrowRight,
  ArrowUp,
  ArrowUpRight,
  Bookmark,
  Check,
  ChevronDown,
  ChevronsUp,
  Download,
  GripVertical,
  Layers,
  Loader2,
  Pencil,
  Plus,
  Trash2,
  Maximize2,
  Minimize2,
  Search,
  Settings,
  Star,
  StarOff,
  X,
} from 'lucide-react'
import { AppShell } from '@/admin/components/app-shell'
import api from '@/admin/lib/api'
import { cn } from '@/shared/lib/utils'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import { DatePicker } from '@/shared/components/ui/date-picker'
import { Button } from '@/shared/components/ui/button'
import { Label } from '@/shared/components/ui/label'
import { Input } from '@/shared/components/ui/input'
import { useDashboardCatalog, useDashboardConfig } from '@/admin/hooks/use-dashboard-config'
import { cardTitle } from './card-titles'
import type {
  DashboardConfigPayload,
  FavDef,
  RoleDef,
  TileDef,
} from './dashboard-types'
import { TileBody, type DrillField, type DrillHandler, type DrillRecords } from './dashboard-tiles'
import { LiveTable } from './live-table'
import { getLiveSource } from './live-sources'
import { TileHeaderSlot } from './tile-header-slot'
import { TimeFilter, type TimeRange } from '@/admin/components/shared/time-filter'
import { timeRangeToParams } from '@/admin/lib/time-range-params'
import { useUserPreference } from '@/admin/hooks/use-user-preference'
import { usePinnedView } from '@/admin/hooks/use-pinned-view'
import { PinMyViewBar } from '@/admin/components/shared/pin-my-view'
import { CardEditor } from './card-editor'
import { useFeatureFlags } from '@/admin/hooks/use-feature-flag'
import { usePermissions } from '@/admin/hooks/use-permissions'
import { useSession } from '@/admin/hooks/use-session'
import { ReportingSection } from './reporting-section'
import { PowerBiConnectDialog } from './powerbi-connect-dialog'
import { DashboardViewDialog, type DashboardViewDraft } from './dashboard-view-dialog'
import { useOrgMembers } from '@/admin/hooks/use-claims'
import { TABLE_PREFERENCES_CHANGED_EVENT } from '@/admin/hooks/use-table-column-preferences'
import {
  applyDashboardViewPrefs,
  dashboardViewTableId,
  parseDashboardViewConfig,
  restrictToKnownCards,
  sameDashboardView,
  snapshotDashboardView,
} from './dashboard-views'
import { useOrg } from '@/admin/hooks/use-org'
import {
  useCreateTableView,
  useDeleteTableView,
  useTableViews,
  useUpdateTableView,
} from '@/admin/hooks/use-table-views'
import type { TableView } from '@/admin/types/table-views'
import { ConfirmDeleteDialog } from '@/admin/components/confirm-delete-dialog'
import { ShowMeHowTo } from '@/admin/components/tour/show-me-how-to'

/**
 * ======================================================================
 * MASTER FRAMEWORK - the generic dashboard experience.
 *
 * Tenant-agnostic: it renders whatever roles / tiles / favourites / brand /
 * suggestions the per-tenant config (GET /api/dashboards/config/) provides.
 * Owens Corning is simply the first customer onboarded - their config is data on
 * the backend, not code here. Onboarding another client is a new config row,
 * never a change to this file. There is no hardcoded brand palette or assistant
 * copy: brand theming reads config.brand, the assistant label/glyph read
 * config.brand.assistant / config.brand.mascot_glyph, and suggestion copy reads
 * config.suggestions - all data.
 * ======================================================================
 */

const SPAN_CLASS: Record<number, string> = {
  4: 'md:col-span-4',
  6: 'md:col-span-6',
  8: 'md:col-span-8',
  12: 'md:col-span-12',
}

// Map the shared TimeFilter selection to a `created_after` date (YYYY-MM-DD) so
// the live tiles (KPIs, trends, breakdowns) reflect the chosen window. Mirrors
// the list pages' time-range semantics; 'all_time' means no lower bound.
function timeRangeToCreatedAfter(
  range: TimeRange,
  custom?: { from: string; to: string },
): string | null {
  return timeRangeToParams(range, custom).created_after ?? null
}

// Report-builder options for the Customise panel. Datasets with an `endpoint`
// download a real CSV over the chosen date range; `view` deep-links to the live
// list. Analytics reports are flagged as coming next.
// `flag` gates the report to tenants whose surface is enabled, so a tenant is
// never offered a report for a surface it doesn't have (e.g. a jewelry tenant
// with no Inspections / Transfers / Lab). Settlements and time-to-resolve are
// claim analytics, so they gate on `claims`; lab turnaround gates on `lab`.
// Absent `flag` = always available.
const CUSTOMISE_REPORTS: {
  key: string
  label: string
  endpoint?: string
  view?: string
  group: 'data' | 'analytics'
  flag?: string
}[] = [
  { key: 'registrations', label: 'Registrations', endpoint: '/registrations/', view: '/registrations', group: 'data', flag: 'registrations' },
  { key: 'claims', label: 'Claims', endpoint: '/claims/claims/', view: '/claims', group: 'data', flag: 'claims' },
  { key: 'inspections', label: 'Inspections', view: '/inspections', group: 'data', flag: 'inspections' },
  { key: 'transfers', label: 'Transfers', view: '/transfers', group: 'data', flag: 'transfers' },
  { key: 'settlements', label: 'Settlements', view: '/claims', group: 'data', flag: 'claims' },
  { key: 'lab', label: 'Lab', view: '/lab', group: 'data', flag: 'lab' },
  { key: 'time_to_resolve', label: 'Time to resolve a claim', group: 'analytics', flag: 'claims' },
  { key: 'lab_turnaround', label: 'Lab turnaround time', group: 'analytics', flag: 'lab' },
]

// A named, per-user saved view: the tile order + removed set under a label.
// Multiple per role (AC5): "save as new view", switch between them, rename,
// remove. Team-shared views are a deliberate fast-follow - first pass is
// per-user (each view lives in this browser's localStorage).
interface SavedView {
  uuid: string
  name: string
  order: string[]
  removed: string[]
}

// Allowed tile widths in the 12-col grid (a third / half / two-thirds / full).
const TILE_WIDTHS = [4, 6, 8, 12] as const

interface RoleState {
  order: string[]
  removed: string[]
  views: SavedView[]
  // null = the department standard (the role's shipped default layout).
  activeViewUuid: string | null
  // Per-user, per-tile width override (tile key -> col span). Absent = the
  // tile's shipped default span.
  spans: Record<string, number>
}

type DashboardState = Record<string, RoleState>

function newViewUuid(): string {
  try {
    return crypto.randomUUID()
  } catch {
    return `v-${Date.now()}-${Math.floor(Math.random() * 1e6)}`
  }
}

function tenantSlug(): string {
  try {
    return sessionStorage.getItem('tenantSlug') ?? 'tenant'
  } catch {
    return 'tenant'
  }
}

// v3: role tilesets + columns re-authored to the OC requirements doc; bump so
// existing saved layouts don't pin the old defaults.
const STORAGE_KEY = () => `raven:dashboard:v3:${tenantSlug()}`

/** A fresh, unedited layout for one role. */
function emptyRoleState(tiles: string[]): RoleState {
  return { order: tiles.slice(), removed: [], views: [], activeViewUuid: null, spans: {} }
}

// Reconcile a saved tile order against the role's current config tileset: keep
// the user's order/removals intact, but append any tile the config has added
// since the layout was saved (unless the user explicitly removed it). Without
// this, a saved layout pins the roster forever and tiles added to a role's
// config - e.g. new KPI tiles - never surface for an existing user.
function reconcileOrder(order: string[], removed: string[], configTiles: string[]): string[] {
  const seen = new Set([...order, ...removed])
  const appended = configTiles.filter((k) => !seen.has(k))
  return appended.length ? [...order, ...appended] : order
}

function defaultState(config: DashboardConfigPayload): DashboardState {
  const s: DashboardState = {}
  config.roles.forEach((r) => {
    s[r.id] = emptyRoleState(r.tiles)
  })
  return s
}

function loadState(config: DashboardConfigPayload): DashboardState {
  const base = defaultState(config)
  try {
    const raw = localStorage.getItem(STORAGE_KEY())
    if (!raw) return base
    // Older persisted shape carried a single `savedOrder` + `activeView`; the
    // reader below migrates it to the named-views model so a user's pinned
    // layout survives the upgrade.
    const saved = JSON.parse(raw) as Record<
      string,
      Partial<RoleState> & { savedOrder?: string[] | null; activeView?: 'standard' | 'my' }
    >
    const known = new Set(Object.keys(config.tiles))
    const clean = (arr?: string[] | null) => (arr ?? []).filter((k) => known.has(k))
    config.roles.forEach((r) => {
      const prior = saved[r.id]
      if (!prior) return
      const order = clean(prior.order)
      let views: SavedView[] = Array.isArray(prior.views)
        ? prior.views.map((v) => ({
            uuid: v.uuid || newViewUuid(),
            name: v.name || 'My view',
            order: clean(v.order),
            removed: clean(v.removed),
          }))
        : []
      let activeViewUuid = prior.activeViewUuid ?? null
      // Migrate the legacy single "my view".
      if (!views.length && prior.savedOrder) {
        const uuid = newViewUuid()
        views = [
          { uuid, name: 'My view', order: clean(prior.savedOrder), removed: clean(prior.removed) },
        ]
        if (prior.activeView === 'my') activeViewUuid = uuid
      }
      if (activeViewUuid && !views.some((v) => v.uuid === activeViewUuid)) activeViewUuid = null
      const spans: Record<string, number> = {}
      if (prior.spans && typeof prior.spans === 'object') {
        for (const [k, v] of Object.entries(prior.spans)) {
          if (known.has(k) && (TILE_WIDTHS as readonly number[]).includes(v as number)) {
            spans[k] = v as number
          }
        }
      }
      const removed = clean(prior.removed)
      base[r.id] = {
        order: reconcileOrder(order.length ? order : r.tiles.slice(), removed, r.tiles),
        removed,
        views,
        activeViewUuid,
        spans,
      }
    })
  } catch {
    /* ignore - fall back to config defaults */
  }
  return base
}

// A dropdown menu (button + popover). Kept generic; content is passed in.
function Menu({
  open,
  onToggle,
  trigger,
  children,
  align = 'left',
  width = 244,
}: {
  open: boolean
  onToggle: () => void
  trigger: React.ReactNode
  children: React.ReactNode
  align?: 'left' | 'right'
  width?: number
}) {
  return (
    <div className="relative">
      <div
        onClick={(e) => {
          e.stopPropagation()
          onToggle()
        }}
      >
        {trigger}
      </div>
      {open && (
        <div
          onClick={(e) => e.stopPropagation()}
          className={cn(
            'absolute top-[calc(100%+8px)] z-40 rounded-[13px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-card)] p-1.5 shadow-[var(--shadow-float)]',
            align === 'right' ? 'right-0' : 'left-0',
          )}
          style={{ minWidth: width }}
        >
          {children}
        </div>
      )}
    </div>
  )
}

function MenuHeading({ children }: { children: React.ReactNode }) {
  return (
    <h4 className="mx-2.5 mb-1 mt-2 text-[10.5px] font-medium uppercase tracking-[0.15em] text-[var(--ink-3)]">
      {children}
    </h4>
  )
}

function MenuItem({
  onClick,
  icon,
  children,
  sub,
  badge,
  swatch,
}: {
  onClick: () => void
  icon?: React.ReactNode
  children: React.ReactNode
  sub?: string
  badge?: string
  swatch?: string
}) {
  return (
    <Button
      variant="ghost"
      onClick={onClick}
      className="flex h-auto w-full items-center justify-start gap-2.5 rounded-lg px-2.5 py-2.5 text-left text-[13px] font-normal text-[var(--ink)] transition-colors hover:bg-[var(--bg-subtle)]"
    >
      {icon != null && <span className="grid w-[18px] place-items-center text-[var(--ink-3)]">{icon}</span>}
      <span className="flex-1">
        {children}
        {sub && <small className="mt-0.5 block text-[11px] text-[var(--ink-3)]">{sub}</small>}
      </span>
      {badge && (
        <span className="rounded-[5px] bg-[var(--gold-bg)] px-1.5 py-0.5 font-mono text-[9.5px] uppercase tracking-[0.06em] text-[var(--gold-ink)]">
          {badge}
        </span>
      )}
      {swatch && <span className="ml-auto h-3.5 w-3.5 rounded" style={{ background: swatch }} />}
    </Button>
  )
}

function MenuSep() {
  return <div className="mx-2 my-1.5 h-px bg-[var(--rule)]" />
}

interface Toast {
  id: number
  msg: string
  kind: 'saved' | 'celebrate'
}

/**
 * Config-fetching shell: loads the tenant's dashboard config, then renders the
 * generic experience. Keeps the engine free of any tenant data - onboarding a
 * new client is a new backend config row, not a code change here.
 */
export function DashboardPage() {
  const { data: config, isPending, isError } = useDashboardConfig()
  if (isPending) {
    return (
      <AppShell>
        <main className="mx-auto max-w-[1180px] px-[clamp(16px,4vw,40px)] py-16">
          <p className="text-[14px] text-[var(--ink-3)]">Loading your dashboard&hellip;</p>
        </main>
      </AppShell>
    )
  }
  if (isError || !config) {
    return (
      <AppShell>
        <main className="mx-auto max-w-[1180px] px-[clamp(16px,4vw,40px)] py-16">
          <p className="text-[14px] text-[var(--ink-3)]">
            Couldn&rsquo;t load your dashboard. Try refreshing.
          </p>
        </main>
      </AppShell>
    )
  }
  return <DashboardExperience config={config} />
}

// The assistant/mascot glyph - a config-driven emoji (config.brand.mascot_glyph),
// NOT a hardcoded per-tenant emoji. Used as the assistant pill icon and header so
// the whole thing reads as one mark. Size is a runtime prop, so font-size is set
// inline (the sanctioned dynamic-value exception). Renders nothing when the
// tenant's config carries no glyph.
function MascotGlyph({ glyph, size = 18 }: { glyph?: string; size?: number }) {
  if (!glyph) return null
  return (
    <span
      aria-hidden="true"
      className="inline-block shrink-0 leading-none"
      style={{ fontSize: `${size}px`, lineHeight: 1 }}
    >
      {glyph}
    </span>
  )
}

function DashboardExperience({ config }: { config: DashboardConfigPayload }) {
  // Config drives the engine. Alias the two collections to the names the body
  // uses throughout so the rest of the component is config-driven with no other
  // change (tenant config now comes from the backend, not a frontend seed).
  const ROLES = config.roles
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [state, setState] = useState<DashboardState>(() => loadState(config))
  // Per-user "profile data": the dashboard layout persisted to the user's
  // profile (server-side, per user + tenant) so it follows them across devices,
  // not just this browser. localStorage remains a fast synchronous cache for the
  // first paint; the server value is the source of truth once it loads.
  const layoutPref = useUserPreference<DashboardState | null>('dashboard-layout-v3', null)
  const layoutHydrated = useRef(false)
  const stateRef = useRef(state)
  useEffect(() => {
    stateRef.current = state
  }, [state])
  // The role is resolved server-side from the user's own role (config.active_role)
  // and is fixed for the session - there is no client-side role switcher. Falls
  // back to the single returned role, then a neutral default.
  const role = config.active_role ?? config.roles[0]?.id ?? 'all'
  const { data: session } = useSession()
  const [customizing, setCustomizing] = useState(false)
  // Lock layout: the same per-user lock the record pages share. Locked holds the
  // tiles still so their numbers and titles can be selected and copied.
  // Customise (the gear) still arranges while locked - it is an explicit ask.
  const cardsView = usePinnedView('dashboard')
  const tilesLocked = cardsView.pinned && !customizing
  // Customise: the card builder (add / edit / delete cards). The report builder
  // (date range + dataset -> View / Download) now lives under Export & integrate.
  const [customiseOpen, setCustomiseOpen] = useState(false)
  const [reportOpen, setReportOpen] = useState(false)
  const [powerBiOpen, setPowerBiOpen] = useState(false)
  const [reportType, setReportType] = useState('registrations')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [downloading, setDownloading] = useState(false)
  const [expandedKey, setExpandedKey] = useState<string | null>(null)
  // Period selector - the shared TimeFilter used across list pages (Registrations
  // etc.), replacing the bespoke range picker. Drives the KPI + dataset windows.
  const [timeRange, setTimeRange] = useState<TimeRange>('all_time')
  const [customRange, setCustomRange] = useState<{ from: string; to: string } | undefined>(undefined)
  const handleTimeFilterChange = (r: TimeRange, custom?: { from: string; to: string }) => {
    setTimeRange(r)
    setCustomRange(custom)
  }
  const [openMenu, setOpenMenu] = useState<string | null>(null)
  const [assistOpen, setAssistOpen] = useState(false)
  // Assistant "find a record" box - resolves a claim or registration number to
  // its detail page. Lookup is generic: claims via the claims search filter,
  // registrations via the shared host search (matches registration numbers).
  const [lookupTerm, setLookupTerm] = useState('')
  const [lookingUp, setLookingUp] = useState(false)
  const [lookupError, setLookupError] = useState<string | null>(null)
  const [addOpen, setAddOpen] = useState(false)
  const [drawer, setDrawer] = useState<{
    ey: string
    title: string
    fields: DrillField[]
    records?: DrillRecords
  } | null>(null)
  const [toasts, setToasts] = useState<Toast[]>([])
  // Mascot rests while the user is active; after a spell of inactivity it
  // drifts into a "thinking" gesture.
  const [idle, setIdle] = useState(false)
  const toastId = useRef(0)
  const firstReorder = useRef(true)

  const roleObj: RoleDef = useMemo(() => ROLES.find((r) => r.id === role) ?? ROLES[0], [ROLES, role])
  // The config resolves to one role, which can differ between loads (the user's
  // role, or the primary default). `state` is seeded once, so fall back to a
  // fresh layout for a role it hasn't seen yet; an effect then persists it.
  const rs = state[role] ?? emptyRoleState(roleObj?.tiles ?? [])

  // Hydrate the layout from the user's profile once it loads: the server value
  // wins over the localStorage-seeded default. If the profile has nothing yet
  // (first time on this account), migrate the browser's existing layout up so it
  // is not lost. Runs once per mount.
  useEffect(() => {
    if (!layoutPref.isLoaded || layoutHydrated.current) return
    layoutHydrated.current = true
    const saved = layoutPref.value
    if (saved && typeof saved === 'object' && Object.keys(saved).length > 0) {
      // The server layout wins, but reconcile each role's order against the
      // current config so tiles added since the layout was saved (e.g. new KPI
      // tiles) still surface instead of being pinned out by the stale roster.
      setState((prev) => {
        const merged: DashboardState = { ...prev, ...saved }
        config.roles.forEach((r) => {
          const s = merged[r.id]
          if (!s) return
          merged[r.id] = {
            ...s,
            order: reconcileOrder(s.order ?? [], s.removed ?? [], r.tiles),
          }
        })
        return merged
      })
    } else {
      layoutPref.setValue(stateRef.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layoutPref.isLoaded])

  // Persist layout changes: localStorage (fast cache) immediately, and the
  // user's profile (debounced) so it follows them across devices. The profile
  // write waits until after hydration so it never echoes the initial load.
  const layoutSaveTimer = useRef<number | null>(null)
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY(), JSON.stringify(state))
    } catch {
      /* storage disabled - layout just won't persist locally */
    }
    if (!layoutHydrated.current) return
    if (layoutSaveTimer.current) window.clearTimeout(layoutSaveTimer.current)
    layoutSaveTimer.current = window.setTimeout(() => {
      layoutSaveTimer.current = null
      layoutPref.setValue(state)
    }, 800)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state])
  // Leaving the dashboard saves a change still waiting, so a card added just
  // before opening a record is still there on the way back.
  useEffect(
    () => () => {
      if (layoutSaveTimer.current === null) return
      window.clearTimeout(layoutSaveTimer.current)
      layoutPref.setValue(stateRef.current)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  // Theming: the dashboard inherits the tenant's GLOBAL theme (accent + fonts),
  // the same one the app's theme runtime stamps on :root from tenant config and
  // that every other admin surface honours. We deliberately do NOT re-stamp
  // --green / --font-* from config.brand here - doing so overrode the tenant
  // theme and made the dashboard inconsistent with the rest of the admin. The
  // brand payload is still used for non-theme data (name / mascot / assistant).

  // Capability flags gate the Export & integrate menu so a tenant is never shown
  // an export / integration for a surface it doesn't have (e.g. Luthmore has no
  // Claims, BI integrations, or public API).
  const flags = useFeatureFlags()
  const hasClaims = flags['claims'] === true
  const hasRegistrations = flags['registrations'] === true
  const hasPublicApi = flags['public_api'] === true

  // The Reporting section (standard reports + exports) is gated on the
  // reports.view permission (org admins / staff auto-pass). Permission-based,
  // not flag-based - the platform retired the `reporting` flag (organisations
  // migration 0101).
  const { can } = usePermissions()
  const canViewReports = can('reports.view')

  // Only offer reports for surfaces this tenant has enabled, so the Reports
  // dialog never lists a report for a surface the tenant lacks (e.g. a jewelry
  // tenant with no Inspections / Transfers / Lab). Mirrors the Export menu and
  // shortcut gating.
  const visibleReports = CUSTOMISE_REPORTS.filter(
    (r) => !r.flag || flags[r.flag] === true,
  )
  // If the selected report is for a surface this tenant lacks, fall back to the
  // first available one so the dialog never opens on a hidden report.
  useEffect(() => {
    if (visibleReports.length && !visibleReports.some((r) => r.key === reportType)) {
      setReportType(visibleReports[0].key)
    }
  }, [visibleReports, reportType])

  const toast = useCallback((msg: string, kind: 'saved' | 'celebrate' = 'saved') => {
    const id = ++toastId.current
    setToasts((t) => [...t, { id, msg, kind }])
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), kind === 'celebrate' ? 3800 : 2200)
  }, [])

  // Favourites: one shared state, the "Add a shortcut" control in the header and
  // the pinned chips on their own row below it.
  const fav = useFavourites(config.favourites, toast)

  // User-authored / edited cards live in the profile (per-user), layered over the
  // role's seeded tiles by key - so an edit shadows the seeded card, and a new
  // card is added on top, without touching the shared config.
  const cardsPref = useUserPreference<Record<string, TileDef>>('dashboard-cards-v1', {})
  // A card the user built or edited shows the name they gave it, else one from
  // its data (in the tenant's words) - never a blank header (WPS-232 #3).
  const { data: catalog } = useDashboardCatalog()
  const TILES = useMemo(() => {
    const userCards = Object.fromEntries(
      Object.entries(cardsPref.value ?? {}).map(([k, card]) => [
        k,
        { ...card, title: cardTitle(card, catalog) || config.tiles[k]?.title || '' },
      ]),
    )
    return { ...config.tiles, ...userCards }
  }, [config.tiles, cardsPref.value, catalog])

  const patchRole = useCallback(
    (mut: (r: RoleState) => RoleState) => {
      setState((prev) => ({
        ...prev,
        [role]: mut(prev[role] ?? emptyRoleState(roleObj?.tiles ?? [])),
      }))
    },
    [role, roleObj],
  )

  // Seed a layout entry for the resolved role if `state` (initialised once) does
  // not have one yet, so persistence and reorder refs have something to write.
  useEffect(() => {
    setState((prev) => (prev[role] ? prev : { ...prev, [role]: emptyRoleState(roleObj?.tiles ?? []) }))
  }, [role, roleObj])

  // ---- Saved views (WPS-232 #1) ----
  // Named snapshots of this role's dashboard, stored as generic saved views
  // under "dashboard:<role>" and optionally shared with the user's organisation.
  // See dashboard-views.tsx for what a view captures.
  const { data: org } = useOrg()
  const tenantSlug = org?.organisation?.tenant_slug ?? null
  const viewTableId = dashboardViewTableId(role)
  const { data: savedViews = [], refetch: refetchViews } = useTableViews(viewTableId)
  const createView = useCreateTableView()
  const updateView = useUpdateTableView()
  const deleteView = useDeleteTableView()
  const myViews = savedViews.filter((v) => v.is_owner)
  // Anything listed that isn't mine was shared with me (by name or with everyone).
  const sharedViews = savedViews.filter((v) => !v.is_owner && !v.is_org_default)
  const activeView = savedViews.find((v) => v.uuid === rs.activeViewUuid) ?? null
  // Bumped when a view is applied, to remount the cards so they re-read the
  // filters / columns the view restored.
  const [viewEpoch, setViewEpoch] = useState(0)
  const [viewDialog, setViewDialog] = useState<'create' | 'edit' | null>(null)
  const [deletingView, setDeletingView] = useState<TableView | null>(null)
  // Colleagues for the share picker: loaded once the Views menu is first
  // opened, so the dialog opens with them already in hand.
  const [colleaguesWanted, setColleaguesWanted] = useState(false)
  const orgUuid = org?.organisation?.uuid ?? null
  const { data: orgMembers = [] } = useOrgMembers(colleaguesWanted ? orgUuid : null, {
    pageSize: 1000,
  })
  const myUserUuid = session?.user?.uuid
  const colleagues = useMemo(
    () =>
      orgMembers
        .filter((m) => m.user_uuid && m.user_uuid !== myUserUuid)
        .map((m) => ({
          value: m.user_uuid,
          label: m.user_name ? `${m.user_name} (${m.user_email})` : m.user_email,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [orgMembers, myUserUuid],
  )
  const orgName = org?.organisation?.name ?? t('dashboard.views.yourOrganisation', 'your organisation')

  // Card filters / columns live in each card (browser storage), so listen for
  // their writes to know when the dashboard drifts from the active view.
  const [prefsTick, setPrefsTick] = useState(0)
  useEffect(() => {
    const bump = () => setPrefsTick((n) => n + 1)
    window.addEventListener(TABLE_PREFERENCES_CHANGED_EVENT, bump)
    return () => window.removeEventListener(TABLE_PREFERENCES_CHANGED_EVENT, bump)
  }, [])

  const currentViewConfig = useCallback(
    () =>
      snapshotDashboardView({
        layout: { order: rs.order, removed: rs.removed, spans: rs.spans ?? {} },
        userCards: cardsPref.value ?? {},
        tenantSlug,
      }),
    [rs.order, rs.removed, rs.spans, cardsPref.value, tenantSlug],
  )
  // Whether the dashboard differs from the view it is showing - drives the
  // "Save changes" prompt next to the Views button.
  const viewDirty = useMemo(() => {
    void prefsTick
    const parsed = activeView ? parseDashboardViewConfig(activeView.config) : null
    if (!parsed) return false
    const saved = restrictToKnownCards(parsed, (k) => !!config.tiles[k] || !!cardsPref.value?.[k])
    return !sameDashboardView(saved, currentViewConfig())
  }, [activeView, currentViewConfig, prefsTick, config.tiles, cardsPref.value])

  // The standard dashboard after the user changed it (added, removed, moved or
  // resized a card) - the moment to offer "Save as a view".
  const standardChanged = useMemo(() => {
    if (activeView) return false
    const standard = roleObj?.tiles ?? []
    return (
      rs.order.length !== standard.length ||
      rs.order.some((k, i) => k !== standard[i]) ||
      rs.removed.length > 0 ||
      Object.keys(rs.spans ?? {}).length > 0
    )
  }, [activeView, roleObj, rs.order, rs.removed, rs.spans])

  const hasUnsavedViewChanges = (activeView ? activeView.is_owner && viewDirty : false) || standardChanged

  // The user's default view: the one the dashboard opens with. Per user, per
  // role dashboard, server-backed. None = the Standard view.
  const defaultViewPref = useUserPreference<Record<string, string | null>>('dashboard-pinned-view-v1', {}, {
    fresh: true,
  })
  const defaultViewUuid = defaultViewPref.value?.[role] ?? null
  const defaultView = savedViews.find((v) => v.uuid === defaultViewUuid) ?? null
  const setDefaultView = (uuid: string | null) =>
    defaultViewPref.setValue({ ...(defaultViewPref.value ?? {}), [role]: uuid })
  // Make the next view saved from the dialog the default (a changed Standard view).
  const defaultOnNextSave = useRef(false)

  // Open with the default view, once per visit, as soon as views have loaded.
  const openedDefault = useRef(false)
  const refetchedViews = useRef(false)
  useEffect(() => {
    if (openedDefault.current || !layoutPref.isLoaded || !defaultViewPref.isLoaded) return
    if (defaultViewUuid && !defaultView) {
      // A cached list can predate the view (made just now, or on another
      // device): ask for the list once rather than wait on it.
      if (!refetchedViews.current) {
        refetchedViews.current = true
        void refetchViews()
      }
      return
    }
    openedDefault.current = true
    if (defaultView && rs.activeViewUuid !== defaultView.uuid) applyView(defaultView, { quiet: true })
    // No default: open the Standard view rather than whichever view was open last.
    else if (!defaultViewUuid && rs.activeViewUuid) applyView(null, { quiet: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layoutPref.isLoaded, defaultViewPref.isLoaded, defaultViewUuid, defaultView])

  // The current view is the default (the Standard view counts when there is no
  // default and it is unchanged).
  const onDefaultView = activeView
    ? activeView.uuid === defaultViewUuid
    : !defaultViewUuid && !standardChanged

  // Views menu: Open this view by default / Stop opening this view by default.
  const toggleDefaultView = () => {
    setOpenMenu(null)
    if (onDefaultView) {
      setDefaultView(null)
      toast(t('dashboard.views.unpinned', 'The Standard view will open first'))
    } else if (activeView) {
      // Keep what's on screen: your own view's changes are saved too.
      if (activeView.is_owner && viewDirty) void saveActiveViewChanges()
      setDefaultView(activeView.uuid)
      toast(
        t('dashboard.views.pinned', { defaultValue: '"{{name}}" will open first', name: activeView.name }),
        'saved',
      )
    } else if (standardChanged) {
      // A changed Standard view has to become a view before it can open first.
      defaultOnNextSave.current = true
      setViewDialog('create')
    } else {
      setDefaultView(null)
      toast(t('dashboard.views.unpinned', 'The Standard view will open first'))
    }
  }

  /** The line under a view in the menu: opens first, then who can open it. */
  const viewLine = (v: TableView): string =>
    v.uuid === defaultViewUuid
      ? t('dashboard.views.pinnedLine', { defaultValue: 'Opens first. {{share}}', share: shareLine(v) })
      : shareLine(v)

  /** One line under a view in the menu: who it is shared with. */
  const shareLine = (v: TableView): string => {
    if (!v.is_owner) {
      return t('dashboard.views.sharedBy', { defaultValue: 'Shared by {{name}}', name: v.owner_name })
    }
    if (v.share_scope === 'organisation') {
      return t('dashboard.views.sharedWithOrg', { defaultValue: 'Shared with everyone at {{org}}', org: orgName })
    }
    if (v.share_scope === 'people' && v.shared_with?.length) {
      // Full names - first names alone ("Test and Test") are ambiguous.
      const first = v.shared_with.slice(0, 2).map((p) => p.name)
      const more = v.shared_with.length - first.length
      return more > 0
        ? t('dashboard.views.sharedWithMore', {
            defaultValue: 'Shared with {{names}} and {{count}} more',
            names: first.join(', '),
            count: more,
          })
        : t('dashboard.views.sharedWithNames', { defaultValue: 'Shared with {{names}}', names: first.join(' and ') })
    }
    return t('dashboard.views.onlyYou', 'Only you')
  }

  const applyView = (view: TableView | null, opts?: { quiet?: boolean }) => {
    setOpenMenu(null)
    if (view === null) {
      // The role's standard layout: the cards the dashboard ships with, with
      // every card's filters and columns cleared - a true clean slate.
      const standard = roleObj?.tiles ?? []
      applyDashboardViewPrefs(
        { version: 1, layout: { order: standard, removed: [], spans: {} }, cards: {}, tilePrefs: {} },
        tenantSlug,
      )
      patchRole((r) => ({ ...emptyRoleState(standard), views: r.views, activeViewUuid: null }))
      setViewEpoch((e) => e + 1)
      if (!opts?.quiet) toast(t('dashboard.views.showingStandard', 'Showing the standard dashboard'))
      return
    }
    const cfg = parseDashboardViewConfig(view.config)
    if (!cfg) {
      toast(t('dashboard.views.unreadable', 'That view could not be opened'))
      return
    }
    // A view built from cards the user doesn't have yet (a teammate's shared
    // view) brings those cards with it.
    if (Object.keys(cfg.cards).length) {
      cardsPref.setValue({ ...(cardsPref.value ?? {}), ...cfg.cards })
    }
    const shown = restrictToKnownCards(cfg, (k) => !!config.tiles[k] || !!cardsPref.value?.[k])
    applyDashboardViewPrefs(shown, tenantSlug)
    patchRole((r) => ({
      ...r,
      order: shown.layout.order,
      removed: shown.layout.removed,
      spans: shown.layout.spans,
      activeViewUuid: view.uuid,
    }))
    setViewEpoch((e) => e + 1)
    if (!opts?.quiet) {
      toast(t('dashboard.views.showing', { defaultValue: 'Showing "{{name}}"', name: view.name }))
    }
  }

  const saveNewView = async (draft: DashboardViewDraft) => {
    const created = await createView.mutateAsync({
      table_id: viewTableId,
      name: draft.name,
      config: currentViewConfig() as unknown as Record<string, unknown>,
      share_scope: draft.scope,
      shared_with: draft.people,
    })
    patchRole((r) => ({ ...r, activeViewUuid: created.uuid }))
    if (defaultOnNextSave.current) {
      defaultOnNextSave.current = false
      setDefaultView(created.uuid)
    }
    setViewDialog(null)
  }

  const saveActiveViewChanges = async () => {
    if (!activeView) return
    setOpenMenu(null)
    await updateView.mutateAsync({
      uuid: activeView.uuid,
      tableId: viewTableId,
      config: currentViewConfig() as unknown as Record<string, unknown>,
    })
    toast(t('dashboard.views.updated', { defaultValue: 'Saved changes to "{{name}}"', name: activeView.name }), 'saved')
  }

  const editActiveView = async (draft: DashboardViewDraft) => {
    if (!activeView) return
    await updateView.mutateAsync({
      uuid: activeView.uuid,
      tableId: viewTableId,
      name: draft.name,
      share_scope: draft.scope,
      shared_with: draft.people,
    })
    setViewDialog(null)
    toast(
      draft.scope === 'private'
        ? t('dashboard.views.saved', { defaultValue: 'Saved "{{name}}"', name: draft.name })
        : t('dashboard.views.nowShared', { defaultValue: '"{{name}}" is shared', name: draft.name }),
      'saved',
    )
  }

  const confirmDeleteView = async () => {
    if (!deletingView) return
    const gone = deletingView
    setDeletingView(null)
    await deleteView.mutateAsync({ uuid: gone.uuid, tableId: viewTableId })
    if (rs.activeViewUuid === gone.uuid) patchRole((r) => ({ ...r, activeViewUuid: null }))
    if (defaultViewUuid === gone.uuid) setDefaultView(null)
    toast(t('dashboard.views.deleted', { defaultValue: 'Deleted "{{name}}"', name: gone.name }))
  }

  // Resolve a typed claim or registration number to its detail page. Both list
  // endpoints expose a substring `search` filter. A claim number matches the
  // stored claim_number; a registration reference is usually derived from the
  // holding UUID (REG- + first 8 chars), so we also try a prefix-stripped term
  // (e.g. "REG-8703D422" -> "8703D422") which matches the UUID text. The prefix
  // picks which resource to try first; otherwise claims then registrations.
  const findRecord = useCallback(async () => {
    const q = lookupTerm.trim()
    if (!q || lookingUp) return
    setLookingUp(true)
    setLookupError(null)

    const core = q.replace(/^(clm|reg)[-\s]*/i, '').trim()
    const terms = core && core.toLowerCase() !== q.toLowerCase() ? [q, core] : [q]

    const tryClaim = async (term: string) => {
      const { data } = await api.get('/claims/claims/', { params: { search: term, page_size: 1 } })
      const uuid = data?.results?.[0]?.uuid
      if (uuid) {
        navigate(`/claims/${uuid}`)
        return true
      }
      return false
    }
    const tryRegistration = async (term: string) => {
      const { data } = await api.get('/registrations/', { params: { search: term, page_size: 1 } })
      const uuid = data?.results?.[0]?.uuid
      if (uuid) {
        navigate(`/registrations/${uuid}`)
        return true
      }
      return false
    }

    const looksLikeReg = /^reg|regist/i.test(q)
    const steps = looksLikeReg ? [tryRegistration, tryClaim] : [tryClaim, tryRegistration]

    let found = false
    let errored = false
    outer: for (const term of terms) {
      for (const step of steps) {
        try {
          if (await step(term)) {
            found = true
            break outer
          }
        } catch {
          errored = true
        }
      }
    }
    if (found) {
      setAssistOpen(false)
      setLookupTerm('')
    } else if (errored) {
      setLookupError('Something went wrong looking that up - please try again.')
    } else {
      setLookupError(`No claim or registration found for "${q}".`)
    }
    setLookingUp(false)
  }, [lookupTerm, lookingUp, navigate])

  // The date window the period selector resolves to; live tiles (KPIs, trends,
  // breakdowns) filter their real data by this.
  const createdAfter = useMemo(
    () => timeRangeToCreatedAfter(timeRange, customRange),
    [timeRange, customRange],
  )

  const activeReport =
    visibleReports.find((r) => r.key === reportType) ?? visibleReports[0]

  // Open the selected dataset's live list, carrying the chosen date window so
  // the list lands pre-filtered.
  const viewReport = useCallback(() => {
    if (!activeReport?.view) {
      toast('That report view is coming next')
      return
    }
    const qs = new URLSearchParams()
    if (dateFrom) qs.set('created_after', dateFrom)
    if (dateTo) qs.set('created_before', dateTo)
    const suffix = qs.toString() ? `?${qs.toString()}` : ''
    setReportOpen(false)
    navigate(`${activeReport.view}${suffix}`)
  }, [activeReport, dateFrom, dateTo, navigate, toast])

  // Download a dataset endpoint as a real CSV (opens in Excel), optionally over
  // a date window. Shared by the Customise report panel and the header
  // Export & integrate menu so both produce a real file, not a toast.
  const exportCsv = useCallback(
    async (endpoint: string, filename: string, after?: string, before?: string) => {
      setDownloading(true)
      try {
        const params: Record<string, string> = { download: 'csv', page_size: '10000' }
        if (after) params.created_after = after
        if (before) params.created_before = before
        const res = await api.get(endpoint, { params, responseType: 'blob' })
        const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }))
        const link = document.createElement('a')
        link.href = url
        link.download = filename
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)
        toast('Export downloaded', 'celebrate')
      } catch {
        toast('Could not generate that export - please try again')
      } finally {
        setDownloading(false)
      }
    },
    [toast],
  )

  // Download the selected Customise dataset over the chosen date range. Datasets
  // without an export endpoint (and analytics) are flagged as coming next.
  const downloadReport = useCallback(() => {
    if (!activeReport?.endpoint) {
      toast(`Export for "${activeReport?.label ?? 'this report'}" is coming next`)
      return
    }
    void exportCsv(activeReport.endpoint, `${activeReport.key}-report.csv`, dateFrom, dateTo)
  }, [activeReport, dateFrom, dateTo, exportCsv, toast])

  const afterReorder = useCallback(
    (celebrateMsg: string, normalMsg: string) => {
      if (firstReorder.current) {
        firstReorder.current = false
        toast(celebrateMsg, 'celebrate')
      } else {
        toast(normalMsg, 'saved')
      }
    },
    [toast],
  )

  // ---- Drag reorder (pointer-based, rect hit-testing; works on touch too) ----
  const gridRef = useRef<HTMLDivElement | null>(null)
  const dragRef = useRef<{ key: string; order: string[] | null } | null>(null)
  const orderRef = useRef<string[]>(rs.order)
  useEffect(() => {
    orderRef.current = rs.order
  }, [rs.order])

  // Idle detection for the mascot: active use keeps it resting; ~7s of no
  // input lets it slip into its thinking gesture.
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>
    const reset = () => {
      setIdle(false)
      clearTimeout(timer)
      timer = setTimeout(() => setIdle(true), 7000)
    }
    const events = ['pointermove', 'pointerdown', 'keydown', 'scroll', 'wheel']
    events.forEach((e) => window.addEventListener(e, reset, { passive: true }))
    reset()
    return () => {
      clearTimeout(timer)
      events.forEach((e) => window.removeEventListener(e, reset))
    }
  }, [])

  // Esc closes the focused (expanded) tile and the detail drawer.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setExpandedKey(null)
        setDrawer(null)
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [])

  useEffect(() => {
    const grid = gridRef.current
    if (!grid) return
    // Pinned: never attach the listener, so a pointerdown on a tile is an
    // ordinary text selection rather than the start of a drag.
    if (tilesLocked) return
    function onDown(e: PointerEvent) {
      if (e.button !== 0) return
      const target = e.target as HTMLElement | null
      if (!target) return
      if (target.closest('button, a, input, select, textarea')) return
      if (target.closest('[data-resize-handle]')) return
      const tileEl = target.closest('[data-tile-id]') as HTMLElement | null
      if (!tileEl) return
      const key = tileEl.getAttribute('data-tile-id')
      if (!key || key === '__add') return
      const startX = e.clientX
      const startY = e.clientY
      let started = false
      // A ghost of the dragged card - the exact size of the source tile - lifted
      // from where it was grabbed and following the cursor, so the drag reads as
      // "I'm carrying this card", not just a dimmed source + a drop ring.
      let ghost: HTMLElement | null = null
      const startRect = tileEl.getBoundingClientRect()
      const grabX = startX - startRect.left
      const grabY = startY - startRect.top
      dragRef.current = { key, order: null }

      // Latest pointer position (viewport coords), kept fresh so the auto-scroll
      // loop can recompute the drop target while the pointer is held still at an
      // edge and the page scrolls underneath it.
      let lastX = startX
      let lastY = startY
      let rafId = 0

      function clearTargets() {
        grid!.querySelectorAll('[data-drop-target="1"]').forEach((n) => n.removeAttribute('data-drop-target'))
      }
      function positionGhost() {
        if (ghost) ghost.style.transform = `translate(${lastX - grabX}px, ${lastY - grabY}px)`
      }
      // Work out the resulting order from the current pointer position, and store
      // it so the drop just applies it. Two cases so a drop works BOTH on a card
      // and on empty space:
      //  - Over an existing card: the dragged card takes that card's slot
      //    (landing on the side it came from), so dropping onto any card always
      //    moves - never a same-slot no-op.
      //  - Over empty space (a gap, an empty half-slot, past the last card): a
      //    row-aware insertion index by cursor position.
      function computeDrop() {
        const prev = orderRef.current
        const o = prev.slice()
        const from = o.indexOf(key!)
        let markerEl: Element | null = null
        if (from > -1) {
          o.splice(from, 1)
          let overEl: HTMLElement | null = null
          grid!.querySelectorAll<HTMLElement>('[data-tile-id]').forEach((n) => {
            const k = n.getAttribute('data-tile-id')
            if (!k || k === key || k === '__add') return
            const r = n.getBoundingClientRect()
            if (lastX >= r.left && lastX <= r.right && lastY >= r.top && lastY <= r.bottom) {
              overEl = n
            }
          })
          let insertAt: number
          if (overEl) {
            const overKey = (overEl as HTMLElement).getAttribute('data-tile-id')!
            insertAt = prev.indexOf(overKey)
            markerEl = overEl
          } else {
            const others = o
              .map((k) => grid!.querySelector<HTMLElement>(`[data-tile-id="${k}"]`))
              .filter((n): n is HTMLElement => !!n)
            insertAt = others.length
            for (let i = 0; i < others.length; i++) {
              const r = others[i].getBoundingClientRect()
              if (lastY < r.top) {
                insertAt = i
                break
              }
              if (lastY <= r.bottom && lastX < r.left + r.width / 2) {
                insertAt = i
                break
              }
            }
            markerEl = others[insertAt] ?? others[others.length - 1] ?? null
          }
          o.splice(Math.max(0, Math.min(insertAt, o.length)), 0, key!)
        }
        clearTargets()
        markerEl?.setAttribute('data-drop-target', '1')
        dragRef.current = { key: key!, order: o.join('|') !== prev.join('|') ? o : null }
      }
      // Auto-scroll the page while the pointer sits near the top / bottom edge,
      // so a card can be dragged to a slot that is currently off-screen. Speed
      // ramps with how deep into the edge zone the pointer is.
      function autoScroll() {
        rafId = requestAnimationFrame(autoScroll)
        if (!started) return
        const EDGE = 70
        const MAX = 22
        const vh = window.innerHeight
        let dy = 0
        if (lastY < EDGE) dy = -Math.ceil(((EDGE - lastY) / EDGE) * MAX)
        else if (lastY > vh - EDGE) dy = Math.ceil(((lastY - (vh - EDGE)) / EDGE) * MAX)
        if (dy !== 0) {
          window.scrollBy(0, dy)
          computeDrop()
        }
      }
      function onMove(ev: PointerEvent) {
        lastX = ev.clientX
        lastY = ev.clientY
        if (!started) {
          if (Math.abs(ev.clientX - startX) + Math.abs(ev.clientY - startY) < 6) return
          started = true
          tileEl!.setAttribute('data-dragging', '1')
          document.body.style.userSelect = 'none'
          ghost = document.createElement('div')
          ghost.className = 'cv-drag-ghost'
          ghost.style.width = `${startRect.width}px`
          ghost.style.height = `${startRect.height}px`
          const label = document.createElement('span')
          label.className = 'cv-drag-ghost-title'
          label.textContent = tileEl!.getAttribute('data-tile-title') || 'Card'
          ghost.appendChild(label)
          document.body.appendChild(ghost)
          rafId = requestAnimationFrame(autoScroll)
        }
        // The stat number opts back in to selection (select-text), which beats
        // the body's userSelect none, so drop any selection the drag drew.
        window.getSelection()?.removeAllRanges()
        positionGhost()
        computeDrop()
      }
      function onUp() {
        document.removeEventListener('pointermove', onMove)
        document.removeEventListener('pointerup', onUp)
        cancelAnimationFrame(rafId)
        document.body.style.userSelect = ''
        tileEl!.removeAttribute('data-dragging')
        if (ghost) {
          ghost.remove()
          ghost = null
        }
        clearTargets()
        const d = dragRef.current
        if (started && d && d.order) {
          const next = d.order
          patchRole((r) => ({ ...r, order: next }))
          afterReorder('Nicely done - this dashboard is yours now.', 'Layout updated')
        }
        dragRef.current = null
      }
      document.addEventListener('pointermove', onMove)
      document.addEventListener('pointerup', onUp)
    }
    grid.addEventListener('pointerdown', onDown)
    return () => grid.removeEventListener('pointerdown', onDown)
  }, [patchRole, afterReorder, tilesLocked])

  // ---- Actions ----
  const WIDTH_LABEL: Record<number, string> = { 4: 'Third', 6: 'Half', 8: 'Two-thirds', 12: 'Full' }
  const cycleTileWidth = (key: string, current: number) => {
    const i = TILE_WIDTHS.indexOf(current as (typeof TILE_WIDTHS)[number])
    const next = TILE_WIDTHS[(i + 1) % TILE_WIDTHS.length]
    patchRole((r) => ({ ...r, spans: { ...r.spans, [key]: next } }))
    toast(`Tile width: ${WIDTH_LABEL[next]}`)
  }

  // The live pixel width of the tile currently being drag-resized (null when not
  // resizing). While set, the tile follows the pointer smoothly (see TileCard);
  // on drop we snap to the nearest allowed span.
  const [resize, setResize] = useState<{ key: string; width: number; startCol: number } | null>(
    null,
  )

  // Drag-to-resize: grab a tile's right edge and pull. The tile grows SMOOTHLY
  // with the pointer (a live pixel width) rather than jumping between spans; only
  // on drop do we snap to the nearest allowed span (third / half / two-thirds /
  // full) and persist it per user.
  const startResize = (key: string, e: React.PointerEvent) => {
    e.preventDefault()
    e.stopPropagation()
    const grid = gridRef.current
    if (!grid) return
    const gridRect = grid.getBoundingClientRect()
    if (gridRect.width <= 0) return
    const tileEl = grid.querySelector(`[data-tile-id="${key}"]`)
    const tileLeft = tileEl ? tileEl.getBoundingClientRect().left : gridRect.left
    const colWidth = gridRect.width / 12
    const minWidth = colWidth * Math.min(...TILE_WIDTHS)
    // The tile's current start column (1-based) - the drag anchors here so the
    // tile grows rightward from where it sits, never jumping to the row's edge.
    const startCol = Math.min(12, Math.max(1, Math.round((tileLeft - gridRect.left) / colWidth) + 1))
    // Available width from the tile's left edge to the end of the grid.
    const maxWidth = gridRect.right - tileLeft
    let width = tileEl ? tileEl.getBoundingClientRect().width : minWidth
    const onMove = (ev: PointerEvent) => {
      // Follow the pointer directly - clamped between the smallest allowed span
      // and the remaining grid width. No snapping here: the tile expands smoothly.
      width = Math.max(minWidth, Math.min(maxWidth, ev.clientX - tileLeft))
      window.getSelection()?.removeAllRanges()
      setResize({ key, width, startCol })
    }
    const onUp = () => {
      document.removeEventListener('pointermove', onMove)
      document.removeEventListener('pointerup', onUp)
      document.body.style.userSelect = ''
      // Snap to the nearest allowed span on drop.
      const rawCols = width / colWidth
      const target = TILE_WIDTHS.reduce((a, b) =>
        Math.abs(b - rawCols) < Math.abs(a - rawCols) ? b : a,
      )
      patchRole((r) => ({ ...r, spans: { ...r.spans, [key]: target } }))
      setResize(null)
      toast(`Tile width: ${WIDTH_LABEL[target]}`)
    }
    document.body.style.userSelect = 'none'
    document.addEventListener('pointermove', onMove)
    document.addEventListener('pointerup', onUp)
  }

  const moveTile = (key: string, dir: 'up' | 'top') => {
    patchRole((r) => {
      const o = r.order.slice()
      const i = o.indexOf(key)
      if (i < 0) return r
      o.splice(i, 1)
      if (dir === 'top') o.unshift(key)
      else o.splice(Math.max(0, i - 1), 0, key)
      return { ...r, order: o }
    })
    afterReorder('Nicely done - this dashboard is yours now.', dir === 'top' ? 'Moved to the top' : 'Moved up')
  }
  const removeTile = (key: string) => {
    patchRole((r) => ({ ...r, order: r.order.filter((k) => k !== key), removed: [...r.removed, key] }))
    toast('Tile removed')
  }
  const addTile = (key: string) => {
    patchRole((r) => ({
      ...r,
      order: r.order.includes(key) ? r.order : [...r.order, key],
      removed: r.removed.filter((k) => k !== key),
    }))
    setAddOpen(false)
    toast(t('dashboard.customise.backOnDashboard', 'Back on your dashboard'))
  }

  const availableToAdd = () => {
    const pool = [...(roleObj.palette ?? []), ...rs.removed]
    const seen = new Set<string>()
    return pool.filter((k) => TILES[k] && !rs.order.includes(k) && !seen.has(k) && seen.add(k))
  }

  // ---- Card builder (per-user add / edit / delete via the Customise modal) ----
  // The card just added in Customise, and the views it has been put in since,
  // so the Customise window can offer "Add to <view>" right where the user is.
  // The card to bring into view (and ring briefly) once Customise closes: the
  // one just added or put back, which otherwise lands at the bottom unseen.
  const [focusKey, setFocusKey] = useState<string | null>(null)
  const [flashKey, setFlashKey] = useState<string | null>(null)
  useEffect(() => {
    if (customiseOpen || !focusKey) return
    const key = focusKey
    // Wait for the dialog to unmount and the card to render, then go to it.
    const timer = window.setTimeout(() => {
      document
        .querySelector(`[data-tile-id="${CSS.escape(key)}"]`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      setFlashKey(key)
      setFocusKey(null)
    }, 150)
    return () => window.clearTimeout(timer)
  }, [customiseOpen, focusKey])
  useEffect(() => {
    if (!flashKey) return
    const timer = window.setTimeout(() => setFlashKey(null), 2400)
    return () => window.clearTimeout(timer)
  }, [flashKey])

  const [justAdded, setJustAdded] = useState<{
    key: string
    title: string
    inViews: string[]
    /** An existing card picked from the list, rather than one just added. */
    existing?: boolean
  } | null>(null)

  // Put one card into a saved view without switching to it: append it to the
  // view's layout and carry the card's definition with the view.
  const addCardToView = async (view: TableView, tile: TileDef) => {
    const cfg = parseDashboardViewConfig(view.config)
    if (!cfg || !view.is_owner) return
    const next = {
      ...cfg,
      layout: {
        ...cfg.layout,
        order: cfg.layout.order.includes(tile.key) ? cfg.layout.order : [...cfg.layout.order, tile.key],
        removed: cfg.layout.removed.filter((k) => k !== tile.key),
      },
      cards: { ...cfg.cards, [tile.key]: tile },
    }
    await updateView.mutateAsync({
      uuid: view.uuid,
      tableId: viewTableId,
      config: next as unknown as Record<string, unknown>,
    })
    setJustAdded((j) => (j && j.key === tile.key ? { ...j, inViews: [...j.inViews, view.uuid] } : j))
  }
  // Save a built card to the user's profile and make sure it is on the board.
  const saveCard = (tile: TileDef) => {
    const existed = !!TILES[tile.key]
    cardsPref.setValue({ ...(cardsPref.value ?? {}), [tile.key]: tile })
    patchRole((r) => ({
      ...r,
      order: r.order.includes(tile.key) ? r.order : [...r.order, tile.key],
      removed: r.removed.filter((k) => k !== tile.key),
    }))
    if (existed) {
      toast(t('dashboard.customise.cardUpdated', 'Card updated'), 'celebrate')
      return
    }
    // A new card: say where it went, and make keeping it in a view one click.
    // On one of your own views it goes straight into that view.
    const title = cardTitle(tile, catalog) || t('dashboard.customise.untitled', 'Untitled')
    setJustAdded({ key: tile.key, title, inViews: [] })
    setFocusKey(tile.key)
    if (activeView?.is_owner) void addCardToView(activeView, tile)
  }
  // Remove a card. A user card is dropped from the profile; a seeded card is
  // hidden via the per-user `removed` list (so it can be restored later).
  const deleteCard = (key: string) => {
    const userCards = cardsPref.value ?? {}
    if (userCards[key]) {
      const next = { ...userCards }
      delete next[key]
      cardsPref.setValue(next)
      patchRole((r) => ({ ...r, order: r.order.filter((k) => k !== key) }))
    } else {
      patchRole((r) => ({
        ...r,
        order: r.order.filter((k) => k !== key),
        removed: [...r.removed, key],
      }))
    }
    toast('Card removed')
  }


  const brandDef = config.brand
  // Branded assistant label: e.g. an "Owen" assistant -> "Ask Owen"; absent ->
  // generic. Config data, never a tenant literal.
  const assistLabel = config.brand?.assistant
    ? `Ask ${config.brand.assistant}`
    : 'Need a hand?'
  // Greet the real logged-in user by first name (personal); fall back to a
  // neutral "there". The role is fixed server-side, so no persona switch.
  const greetName = session?.user?.first_name || 'there'

  return (
    <AppShell>
      <div onClick={() => setOpenMenu(null)}>
        <main className="mx-auto max-w-[1180px] px-[clamp(16px,4vw,40px)] pb-28 pt-[clamp(20px,4vw,40px)]">
          {/* Page head */}
          <div className="mb-3">
            <h1 className="clara-title">
              Good morning, <span className="text-[var(--green)]">{greetName}</span>
            </h1>
            <div className="mt-1 text-[13px] text-[var(--ink-3)]">{roleObj.sub}</div>
          </div>

          {/* Toolbar: time range + Shortcuts control + pinned shortcut buttons all
              on the left, wrapping as needed; Export & Customise on the right. */}
          <div className="mb-4 mt-6 flex items-start justify-between gap-4 border-b-[0.5px] border-[var(--rule)] pb-4">
            <div className="flex min-w-0 flex-wrap items-center gap-2">
              <TimeFilter
                value={timeRange}
                customRange={customRange}
                onChange={handleTimeFilterChange}
              />
              <AddShortcut fav={fav} />
              <FavouriteChips fav={fav} />
            </div>

            <div className="flex shrink-0 items-center gap-2">
              {/* Views: switch between saved snapshots of this dashboard, save
                  the current one, and share it with the team (WPS-232 #1). */}
              <Menu
                open={openMenu === 'views'}
                onToggle={() => {
                  setColleaguesWanted(true)
                  setOpenMenu(openMenu === 'views' ? null : 'views')
                }}
                align="right"
                width={300}
                trigger={
                  <Button
                    variant="ghost"
                    data-testid="dashboard-views-trigger"
                    data-tour="dashboard-views"
                    className="flex h-9 max-w-[240px] items-center gap-2 rounded-[9px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-card)] px-3 text-[13px] font-normal text-[var(--ink)] transition-colors hover:border-[var(--rule-strong)] hover:bg-[var(--bg-subtle)]"
                  >
                    <Layers size={15} />
                    <span className="truncate">
                      {activeView ? activeView.name : t('dashboard.views.standard', 'Standard view')}
                    </span>
                    {/* Unsaved changes: the same small dot the list pages show on a
                        saved view; Save changes is at the top of this menu. */}
                    {hasUnsavedViewChanges && (
                      <span
                        data-testid="dashboard-views-unsaved-dot"
                        title={t('dashboard.views.unsavedChanges', 'Unsaved changes')}
                        className="h-1.5 w-1.5 shrink-0 rounded-full bg-[var(--green)]"
                      >
                        <span className="sr-only">{t('dashboard.views.unsavedChanges', 'Unsaved changes')}</span>
                      </span>
                    )}
                    <ChevronDown size={14} className="shrink-0 text-[var(--ink-3)]" />
                  </Button>
                }
              >
                {hasUnsavedViewChanges && (
                  <>
                    {activeView?.is_owner ? (
                      <MenuItem
                        icon={<span className="h-2 w-2 rounded-full bg-[var(--green)]" />}
                        sub={t('dashboard.views.saveChangesSub', 'You changed the dashboard since saving this view')}
                        onClick={() => void saveActiveViewChanges()}
                      >
                        {t('dashboard.views.saveChanges', { defaultValue: 'Save changes to "{{name}}"', name: activeView.name })}
                      </MenuItem>
                    ) : (
                      <MenuItem
                        icon={<span className="h-2 w-2 rounded-full bg-[var(--green)]" />}
                        sub={t('dashboard.views.saveChangedStandardSub', 'You changed this dashboard. Keep it as a view.')}
                        onClick={() => {
                          setOpenMenu(null)
                          setViewDialog('create')
                        }}
                      >
                        {t('dashboard.views.saveNew', 'Save as new view...')}
                      </MenuItem>
                    )}
                    <MenuSep />
                  </>
                )}
                <MenuHeading>{t('dashboard.views.headingShow', 'Show')}</MenuHeading>
                <MenuItem
                  icon={activeView ? <span /> : <Check size={14} className="text-[var(--green)]" />}
                  sub={
                    defaultViewUuid
                      ? t('dashboard.views.standardSub', 'The cards your role starts with, no filters')
                      : t('dashboard.views.standardPinnedSub', 'Opens first. The cards your role starts with')
                  }
                  onClick={() => applyView(null)}
                >
                  {t('dashboard.views.standard', 'Standard view')}
                </MenuItem>
                {myViews.length > 0 && (
                  <>
                    <MenuSep />
                    <MenuHeading>{t('dashboard.views.headingMine', 'My views')}</MenuHeading>
                    {myViews.map((v) => (
                      <MenuItem
                        key={v.uuid}
                        icon={
                          v.uuid === activeView?.uuid ? (
                            <Check size={14} className="text-[var(--green)]" />
                          ) : (
                            <span />
                          )
                        }
                        sub={viewLine(v)}
                        onClick={() => applyView(v)}
                      >
                        {v.name}
                      </MenuItem>
                    ))}
                  </>
                )}
                {sharedViews.length > 0 && (
                  <>
                    <MenuSep />
                    <MenuHeading>{t('dashboard.views.headingShared', 'Shared with me')}</MenuHeading>
                    {sharedViews.map((v) => (
                      <MenuItem
                        key={v.uuid}
                        icon={
                          v.uuid === activeView?.uuid ? (
                            <Check size={14} className="text-[var(--green)]" />
                          ) : (
                            <span />
                          )
                        }
                        sub={viewLine(v)}
                        onClick={() => applyView(v)}
                      >
                        {v.name}
                      </MenuItem>
                    ))}
                  </>
                )}
                <MenuSep />
                {/* Nothing to offer on the Standard view while it is the default. */}
                {(activeView || !onDefaultView) && (
                  <MenuItem
                    icon={onDefaultView ? <StarOff size={14} /> : <Star size={14} />}
                    sub={
                      onDefaultView
                        ? t('dashboard.views.stopDefaultSub', 'The Standard view opens instead')
                        : !activeView && standardChanged
                          ? t('dashboard.views.makeDefaultNewSub', 'Keep it as a view that opens every time')
                          : t('dashboard.views.makeDefaultSub', 'It opens every time you come to the dashboard')
                    }
                    onClick={toggleDefaultView}
                  >
                    {onDefaultView
                      ? t('dashboard.views.stopDefault', 'Stop opening this view by default')
                      : t('dashboard.views.makeDefault', 'Open this view by default')}
                  </MenuItem>
                )}
                <MenuItem
                  icon={<Plus size={14} />}
                  sub={
                    activeView && !activeView.is_owner
                      ? t('dashboard.views.saveCopySub', 'Make your own copy you can change')
                      : t('dashboard.views.saveNewSub', 'Keep this dashboard to come back to')
                  }
                  onClick={() => {
                    setOpenMenu(null)
                    setViewDialog('create')
                  }}
                >
                  {t('dashboard.views.saveNew', 'Save as new view...')}
                </MenuItem>
                {activeView?.is_owner && (
                  <>
                    <MenuItem
                      icon={<Pencil size={14} />}
                      sub={t('dashboard.views.editSub', 'Change the name or who can open it')}
                      onClick={() => {
                        setOpenMenu(null)
                        setViewDialog('edit')
                      }}
                    >
                      {t('dashboard.views.edit', 'Rename or share...')}
                    </MenuItem>
                    <MenuItem
                      icon={<Trash2 size={14} />}
                      onClick={() => {
                        setOpenMenu(null)
                        setDeletingView(activeView)
                      }}
                    >
                      {t('dashboard.views.delete', { defaultValue: 'Delete "{{name}}"', name: activeView.name })}
                    </MenuItem>
                  </>
                )}
              </Menu>
              <Menu
                open={openMenu === 'export'}
                onToggle={() => setOpenMenu(openMenu === 'export' ? null : 'export')}
                align="right"
                width={260}
                trigger={
                  <Button
                    variant="ghost"
                    className="flex h-9 items-center gap-2 rounded-[9px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-card)] px-3 text-[13px] font-normal text-[var(--ink)] transition-colors hover:border-[var(--rule-strong)] hover:bg-[var(--bg-subtle)]"
                  >
                    <Download size={15} />
                    Export &amp; integrate
                    <ChevronDown size={14} className="text-[var(--ink-3)]" />
                  </Button>
                }
              >
                <MenuHeading>Download (opens in Excel)</MenuHeading>
                {hasClaims && (
                  <MenuItem
                    icon={<span>↓</span>}
                    onClick={() => {
                      setOpenMenu(null)
                      void exportCsv('/claims/claims/', 'claims-export.csv')
                    }}
                  >
                    Claims (CSV)
                  </MenuItem>
                )}
                {hasRegistrations && (
                  <MenuItem
                    icon={<span>↓</span>}
                    onClick={() => {
                      setOpenMenu(null)
                      void exportCsv('/registrations/', 'registrations-export.csv')
                    }}
                  >
                    Registrations (CSV)
                  </MenuItem>
                )}
                <MenuItem
                  icon={<span>↓</span>}
                  onClick={() => {
                    setOpenMenu(null)
                    setReportOpen(true)
                  }}
                >
                  More reports &amp; date range...
                </MenuItem>
                {/* BI connections ride the Public API report feed, so they show
                    only for tenants with the public_api capability - never bleed
                    an integration a tenant hasn't got. */}
                {hasPublicApi && (
                  <>
                    <MenuSep />
                    <MenuHeading>Connect your BI tools</MenuHeading>
                    {canViewReports && (
                      <MenuItem
                        icon={<span>⊞</span>}
                        sub="Report datasets with scheduled refresh"
                        onClick={() => { setOpenMenu(null); setPowerBiOpen(true) }}
                      >
                        Power BI
                      </MenuItem>
                    )}
                    <MenuItem
                      icon={<span className="font-mono text-[11px]">{'{}'}</span>}
                      sub="REST + webhooks"
                      onClick={() => { setOpenMenu(null); navigate('/admin/developers') }}
                    >
                      API access
                    </MenuItem>
                  </>
                )}
              </Menu>
              {/* Customise: icon-only gear, last on the right. Active (green) while
                  arranging so the toggle state still reads without a label. */}
              <Button
                variant="ghost"
                aria-label={customizing ? 'Done arranging' : 'Customise dashboard'}
                data-tour="dashboard-customise"
                title={customizing ? 'Done arranging' : 'Customise dashboard'}
                onClick={() => {
                  if (customizing) setCustomizing(false)
                  else setCustomiseOpen(true)
                }}
                className={cn(
                  'grid h-9 w-9 place-items-center rounded-[9px] border-[0.5px] p-0 transition-colors',
                  customizing
                    ? 'border-[var(--green)] bg-[var(--green-bg)] text-[var(--green)]'
                    : 'border-[var(--rule)] bg-[var(--bg-card)] text-[var(--ink)] hover:border-[var(--rule-strong)] hover:bg-[var(--bg-subtle)]',
                )}
              >
                <Settings size={16} />
              </Button>
            </div>
          </div>

          {/* Reporting: expandable standard-reports + export section, gated to
              admins by reports.view. Sits below the header, above the tiles. */}
          {canViewReports && <ReportingSection />}

          <PinMyViewBar view={cardsView} />

          {/* Grid */}
          <div ref={gridRef} className="cv-tile-grid grid grid-cols-1 gap-4 md:grid-cols-12">
            {rs.order.length === 0 && (
              <div className="col-span-full px-4 py-12 text-center">
                <h3 className="font-display text-[24px] font-medium text-[var(--ink)]">A clean slate.</h3>
                <p className="mt-1.5 text-[var(--ink-3)]">Add tiles to build your view.</p>
              </div>
            )}
            {rs.order.map((key) => {
              const tile = TILES[key]
              if (!tile) return null
              const effectiveSpan = rs.spans?.[key] ?? tile.span
              return (
                <TileCard
                  key={`${key}:${viewEpoch}`}
                  tile={tile}
                  highlighted={flashKey === key}
                  spanClass={SPAN_CLASS[effectiveSpan] ?? SPAN_CLASS[tile.span]}
                  currentSpan={effectiveSpan}
                  dragWidth={resize?.key === key ? resize.width : null}
                  dragStartCol={resize?.key === key ? resize.startCol : null}
                  customizing={customizing}
                  locked={tilesLocked}
                  createdAfter={createdAfter}
                  onExpand={() => setExpandedKey(key)}
                  onMoveUp={() => moveTile(key, 'up')}
                  onMoveTop={() => moveTile(key, 'top')}
                  onCycleWidth={() => cycleTileWidth(key, effectiveSpan)}
                  onResizeStart={(e) => startResize(key, e)}
                  onRemove={() => removeTile(key)}
                  onDrill={(ey, title, fields, records) => setDrawer({ ey, title, fields, records })}
                />
              )
            })}
            {customizing && (
              <Button
                variant="ghost"
                data-tile-id="__add"
                onClick={() => setAddOpen(true)}
                className="col-span-1 grid h-auto min-h-[150px] place-items-center rounded-[15px] border border-dashed border-[var(--rule-strong)] p-0 font-normal text-[var(--ink-3)] transition-colors hover:bg-transparent hover:border-[var(--green)] hover:text-[var(--green)] md:col-span-4"
              >
                <div className="text-center">
                  <div className="font-display text-[30px] leading-none">+</div>
                  <div className="mt-1.5 text-[12px]">Add tile</div>
                </div>
              </Button>
            )}
          </div>
        </main>

        {/* Mascot slot (brand-supplied; original placeholder here). Perches just
            above the assistant: rests while you work, ponders when you pause,
            and opens the assistant on click. Tenants drop in their own licensed
            asset. */}
        {/* Helper: the mascot is perched right on top of the "Need a hand?"
            pill so they read as ONE unit, tucked into the bottom-right beside
            the last card. */}
        <div className="fixed bottom-4 right-4 z-50 flex flex-col items-center sm:bottom-6 sm:right-6">
          {brandDef?.mascot && (
            <>
              {idle && (
                <div className="mb-1.5 flex items-center gap-1 rounded-full border-[0.5px] border-[var(--rule)] bg-[var(--bg-card)] px-2.5 py-1.5 shadow-[var(--shadow-float)]">
                  <span className="h-1.5 w-1.5 rounded-full bg-[var(--green)] animate-[mascotThinkDot_1.4s_ease-in-out_infinite]" />
                  <span className="h-1.5 w-1.5 rounded-full bg-[var(--green)] animate-[mascotThinkDot_1.4s_ease-in-out_infinite] [animation-delay:200ms]" />
                  <span className="h-1.5 w-1.5 rounded-full bg-[var(--green)] animate-[mascotThinkDot_1.4s_ease-in-out_infinite] [animation-delay:400ms]" />
                </div>
              )}
              <Button
                variant="ghost"
                onClick={(e) => {
                  e.stopPropagation()
                  setAssistOpen(true)
                }}
                aria-label={`Assistant mascot - ${assistLabel}`}
                title={assistLabel}
                className={cn(
                  'relative z-10 -mb-2 h-auto w-[54px] origin-bottom rounded-none p-0 drop-shadow-[0_10px_16px_rgba(26,26,24,0.18)] hover:bg-transparent',
                  idle && 'animate-[mascotThink_3.2s_ease-in-out_infinite]',
                )}
              >
                {/* Owen the owlet - cute original placeholder mascot (OC swaps
                    their licensed one later): round body, ear tufts, big
                    eye-discs, tiny beak. Wise + sweet. */}
                <svg viewBox="0 0 100 104" className="w-full" aria-hidden="true">
                  <path d="M28 21 L34 40 L41 25 Z" fill="var(--green)" />
                  <path d="M72 21 L66 40 L59 25 Z" fill="var(--green)" />
                  <ellipse cx="50" cy="60" rx="40" ry="40" fill="var(--green)" />
                  <circle cx="38" cy="56" r="13" fill="#fff" opacity="0.16" />
                  <circle cx="62" cy="56" r="13" fill="#fff" opacity="0.16" />
                  <circle cx="38" cy="56" r="8.5" fill="#fff" />
                  <circle cx="62" cy="56" r="8.5" fill="#fff" />
                  <circle cx="39.5" cy="57.5" r="4" fill="#1a1a18" />
                  <circle cx="63.5" cy="57.5" r="4" fill="#1a1a18" />
                  <circle cx="41.4" cy="55.6" r="1.5" fill="#fff" />
                  <circle cx="65.4" cy="55.6" r="1.5" fill="#fff" />
                  <path d="M46 63 L50 71 L54 63 Z" fill="#fff" opacity="0.85" />
                </svg>
              </Button>
            </>
          )}
          <Button
            variant="ghost"
            onClick={(e) => {
              e.stopPropagation()
              setAssistOpen((v) => !v)
            }}
            className="flex h-[46px] items-center gap-2 rounded-full border-[0.5px] border-[var(--green-border)] bg-[var(--bg-card)] pl-[15px] pr-[18px] text-[13.5px] font-normal text-[var(--ink)] shadow-[var(--shadow-float)] transition-transform hover:-translate-y-0.5 hover:bg-[var(--bg-card)]"
          >
            <MascotGlyph glyph={config.brand?.mascot_glyph} size={19} />
            {assistLabel}
          </Button>
        </div>
        {assistOpen && (
          <div
            onClick={(e) => e.stopPropagation()}
            className="fixed bottom-[124px] right-4 z-50 flex max-h-[calc(100dvh-148px)] w-[min(360px,calc(100vw-44px))] flex-col overflow-hidden rounded-[18px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-card)] shadow-[var(--shadow-float)] sm:right-6"
          >
            {/* Fits any screen height: the body scrolls, the ask box stays put. */}
            <div data-testid="assistant-panel-body" className="min-h-0 flex-1 overflow-y-auto p-6 pb-4">
            {config.brand?.assistant && (
              <p className="mb-2 flex items-center gap-1.5 text-[10.5px] font-medium uppercase tracking-[0.14em] text-[var(--green)]">
                <MascotGlyph glyph={config.brand?.mascot_glyph} size={13} /> {config.brand.assistant}
              </p>
            )}
            <p className="mb-1 font-display text-[21px] leading-tight text-[var(--ink)]">
              Hey <span className="italic text-[var(--green)]">{greetName}</span> - happy to help. What&rsquo;re we
              working on?
            </p>
            <p className="mb-4 text-[13px] text-[var(--ink-2)]">
              Tell me what you need and I&rsquo;ll get you sorted - pull a report, tidy your dashboard, or set up a
              connection.
            </p>
            <div className="mb-4 rounded-[13px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] p-3">
              <Label
                htmlFor="assist-find-record"
                className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.13em] text-[var(--ink-3)]"
              >
                Find a claim or registration
              </Label>
              <div className="flex gap-2">
                <Input
                  id="assist-find-record"
                  value={lookupTerm}
                  onChange={(e) => {
                    setLookupTerm(e.target.value)
                    if (lookupError) setLookupError(null)
                  }}
                  placeholder="e.g. CLM-15F39779 or a registration no"
                  aria-label="Find a claim or registration by number"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') findRecord()
                  }}
                  className="h-10 flex-1 rounded-[11px] border-[0.5px] border-[var(--rule)] bg-[var(--bg)] px-3 text-[13px] text-[var(--ink)]"
                />
                <Button
                  variant="default"
                  size="icon"
                  aria-label="Find record"
                  disabled={lookingUp}
                  onClick={findRecord}
                  className="h-10 w-10 flex-none rounded-[11px]"
                >
                  {lookingUp ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />}
                </Button>
              </div>
              {lookupError && (
                <p className="mt-2 text-[12px] text-[var(--status-error)]">{lookupError}</p>
              )}
            </div>
            <div className="mb-4">
              <ShowMeHowTo currentSection="dashboard" onStart={() => setAssistOpen(false)} />
            </div>
            <div className="flex flex-col gap-1.5">
              {(config.suggestions?.[role] ?? config.suggestions?.all ?? []).map((s) => (
                <Button
                  key={s.t}
                  variant="ghost"
                  onClick={() => {
                    setAssistOpen(false)
                    toast(`On it - ${s.t.toLowerCase()}`)
                  }}
                  className="flex h-auto w-full items-center justify-start gap-2.5 whitespace-normal rounded-[11px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-card)] px-3 py-2.5 text-left text-[13px] font-normal text-[var(--ink)] transition-colors hover:border-[var(--green-border)] hover:bg-[var(--green-bg)]"
                >
                  <span className="grid w-[17px] flex-none place-items-center text-[var(--green)]">{s.i}</span>
                  {s.t}
                </Button>
              ))}
            </div>
            </div>
            <div className="flex flex-none gap-2 border-t-[0.5px] border-[var(--rule)] px-6 pb-6 pt-4">
              <Input
                placeholder="e.g. show my overdue claims"
                aria-label="Ask for help"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    setAssistOpen(false)
                    toast("On it - I'll set that up")
                  }
                }}
                className="h-10 flex-1 rounded-[11px] border-[0.5px] border-[var(--rule)] bg-[var(--bg)] px-3 text-[13px] text-[var(--ink)]"
              />
              <Button
                variant="default"
                size="icon"
                aria-label="Send"
                onClick={() => {
                  setAssistOpen(false)
                  toast('On it - I&rsquo;ll set that up')
                }}
                className="h-10 w-10 rounded-[11px]"
              >
                <ArrowRight size={16} />
              </Button>
            </div>
          </div>
        )}

        {/* Add-tile modal */}
        {addOpen && (
          <div className="fixed inset-0 z-[59] bg-[rgba(20,18,14,0.28)]" onClick={() => setAddOpen(false)}>
            <div
              onClick={(e) => e.stopPropagation()}
              className="absolute left-1/2 top-1/2 min-w-[300px] -translate-x-1/2 -translate-y-1/2 rounded-[13px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-card)] p-1.5 shadow-[var(--shadow-float)]"
            >
              <MenuHeading>Add a tile</MenuHeading>
              {availableToAdd().length ? (
                availableToAdd().map((k) => (
                  <MenuItem key={k} icon={<span>+</span>} onClick={() => addTile(k)}>
                    {TILES[k].title}
                  </MenuItem>
                ))
              ) : (
                <div className="px-3 py-3 text-[13px] text-[var(--ink-3)]">
                  Every available tile is already on your dashboard.
                </div>
              )}
            </div>
          </div>
        )}

        {/* Card builder: add / edit / delete the cards on the dashboard. */}
        <CardEditor
          open={customiseOpen}
          onClose={() => {
            setCustomiseOpen(false)
            setJustAdded(null)
          }}
          order={rs.order}
          tiles={TILES}
          createdAfter={createdAfter}
          onSave={saveCard}
          onDelete={deleteCard}
          justAdded={justAdded}
          views={myViews.map((v) => ({
            uuid: v.uuid,
            name: v.name,
            cardKeys: parseDashboardViewConfig(v.config)?.layout.order ?? [],
          }))}
          userCardKeys={Object.keys(cardsPref.value ?? {})}
          onPutBack={(key) => {
            addTile(key)
            setFocusKey(key)
          }}
          onPickCardForView={(key) => {
            const tile = TILES[key]
            if (!tile) return
            setJustAdded({
              key,
              title: cardTitle(tile, catalog) || t('dashboard.customise.untitled', 'Untitled'),
              inViews: myViews
                .filter((v) => parseDashboardViewConfig(v.config)?.layout.order.includes(key))
                .map((v) => v.uuid),
              existing: true,
            })
          }}
          onAddToView={(uuid) => {
            const view = myViews.find((v) => v.uuid === uuid)
            const tile = justAdded ? TILES[justAdded.key] : undefined
            if (view && tile) void addCardToView(view, tile)
          }}
          onNewView={() => {
            setCustomiseOpen(false)
            setJustAdded(null)
            setViewDialog('create')
          }}
          onRearrange={() => {
            setCustomizing(true)
            toast('Drag any card by its handle to rearrange')
          }}
        />

        {hasPublicApi && canViewReports && (
          <PowerBiConnectDialog open={powerBiOpen} onClose={() => setPowerBiOpen(false)} />
        )}

        {/* Report builder (date range + dataset -> View / Download), opened from
            the Export & integrate menu. */}
        <Dialog open={reportOpen} onOpenChange={setReportOpen}>
          <DialogContent className="max-w-[520px]">
            <DialogHeader>
              <DialogTitle>Reports</DialogTitle>
              <DialogDescription>
                Build a report over a date range, then view it or download it to Excel.
              </DialogDescription>
            </DialogHeader>

            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="cust-report">Report</Label>
                <Select value={reportType} onValueChange={setReportType}>
                  <SelectTrigger id="cust-report">
                    <SelectValue placeholder="Choose a report" />
                  </SelectTrigger>
                  <SelectContent>
                    {visibleReports.map((r) => (
                      <SelectItem key={r.key} value={r.key}>
                        {r.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="flex flex-col gap-1.5">
                  <Label>From</Label>
                  <DatePicker value={dateFrom} onChange={setDateFrom} placeholder="Start date" />
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label>To</Label>
                  <DatePicker value={dateTo} onChange={setDateTo} placeholder="End date" />
                </div>
              </div>

              {activeReport?.group === 'analytics' && (
                <p className="text-[12px] text-[var(--ink-3)]">
                  Analytics reports (time to resolve, lab turnaround) are coming next - the data
                  export is being wired up.
                </p>
              )}

              <div className="flex items-center justify-end gap-2 pt-1">
                <Button variant="outline" onClick={viewReport} disabled={!activeReport?.view}>
                  View
                </Button>
                <Button onClick={downloadReport} disabled={downloading || !activeReport?.endpoint}>
                  {downloading ? 'Preparing...' : 'Download (Excel)'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Detail drawer: a read-only summary of a row with no record behind
            it. Rows that link to a record (href / uuid) open it directly and
            never reach this drawer, so it offers no "open record" action. */}
        <div
          className={cn('fixed inset-0 z-[55] bg-[rgba(20,18,14,0.32)] transition-opacity', drawer ? 'opacity-100' : 'pointer-events-none opacity-0')}
          onClick={() => setDrawer(null)}
        />
        <aside
          className={cn(
            'fixed right-0 top-0 z-[56] flex h-full flex-col border-l-[0.5px] border-[var(--rule)] bg-[var(--bg-card)] transition-transform duration-300',
            drawer?.records ? 'w-[min(880px,96vw)]' : 'w-[min(440px,92vw)]',
            drawer ? 'translate-x-0' : 'translate-x-full',
          )}
          aria-hidden={!drawer}
        >
          <div className="flex items-start gap-3 border-b-[0.5px] border-[var(--rule)] p-6">
            <div>
              <div className="text-[10.5px] uppercase tracking-[0.15em] text-[var(--ink-3)]">{drawer?.ey}</div>
              <h3 className="mt-1 font-display text-[27px] font-medium leading-tight text-[var(--ink)]">
                {drawer?.title}
              </h3>
            </div>
            <Button
              variant="ghost"
              aria-label="Close"
              onClick={() => setDrawer(null)}
              className="ml-auto grid h-9 w-9 flex-none place-items-center rounded-[9px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-card)] p-0 text-[var(--ink-2)] transition-colors hover:bg-[var(--bg-card)] hover:text-[var(--ink)]"
            >
              <X size={15} />
            </Button>
          </div>
          <div className="flex-1 overflow-y-auto p-6">
            {drawer?.fields.map((f, i) => (
              <div
                key={i}
                className="flex justify-between gap-4 border-b-[0.5px] border-[var(--rule)] py-3 text-[13.5px] last:border-b-0"
              >
                <span className="text-[var(--ink-3)]">{f.l}</span>
                <span className="text-right font-medium text-[var(--ink)]">{f.v}</span>
              </div>
            ))}
            {/* The records behind the row: the live list narrowed to this
                segment and the dashboard's date window. Each row opens its record. */}
            {drawer?.records && (
              <div className="mt-6" data-testid="drawer-segment-records">
                <p className="mb-2 text-[10.5px] font-medium uppercase tracking-[0.14em] text-[var(--ink-3)]">
                  {t('dashboard.drawer.records', 'The records behind this number')}
                </p>
                <LiveTable
                  key={`${drawer.records.source}:${drawer.records.segment}:${createdAfter ?? ''}`}
                  source={drawer.records.source}
                  binding={{
                    ...drawer.records.binding,
                    source: drawer.records.source,
                    segment: drawer.records.segment,
                    createdAfter: createdAfter ?? undefined,
                  }}
                  tableId={`dashboard-drawer-${drawer.records.source}`}
                />
              </div>
            )}
          </div>
        </aside>

        {/* Focused (expanded) tile overlay */}
        {expandedKey && TILES[expandedKey] && (
          <ExpandedTile
            tile={TILES[expandedKey]}
            createdAfter={createdAfter}
            onClose={() => setExpandedKey(null)}
            onDrill={(ey, title, fields, records) => setDrawer({ ey, title, fields, records })}
          />
        )}

        <DashboardViewDialog
          open={viewDialog !== null}
          mode={viewDialog ?? 'create'}
          initial={
            viewDialog === 'edit' && activeView
              ? {
                  name: activeView.name,
                  scope: activeView.share_scope ?? 'private',
                  people: (activeView.shared_with ?? []).map((p) => p.uuid),
                }
              : { name: '', scope: 'private', people: [] }
          }
          orgName={orgName}
          colleagues={colleagues}
          isPending={createView.isPending || updateView.isPending}
          onClose={() => {
            defaultOnNextSave.current = false
            setViewDialog(null)
          }}
          onSubmit={viewDialog === 'edit' ? editActiveView : saveNewView}
        />
        {deletingView && (
          <ConfirmDeleteDialog
            open
            onOpenChange={(o) => {
              if (!o) setDeletingView(null)
            }}
            title={t('dashboard.views.deleteTitle', { defaultValue: 'Delete "{{name}}"?', name: deletingView.name })}
            description={
              deletingView.is_shared
                ? t('dashboard.views.deleteSharedDescription', 'Your team will no longer see this view. Your dashboard stays as it is.')
                : t('dashboard.views.deleteDescription', 'This removes the saved view. Your dashboard stays as it is.')
            }
            onConfirm={() => void confirmDeleteView()}
          />
        )}

        {/* Toasts */}
        <div className="fixed bottom-[26px] left-1/2 z-[60] flex -translate-x-1/2 flex-col items-center gap-2">
          {toasts.map((t) => (
            <div
              key={t.id}
              className="flex items-center gap-2.5 rounded-[11px] bg-[var(--ink)] px-4 py-2.5 text-[13px] text-[var(--bg)] shadow-[var(--shadow-float)]"
            >
              <span className="text-[var(--green)]">{t.kind === 'celebrate' ? '✦' : '✓'}</span>
              {t.msg}
            </div>
          ))}
        </div>
      </div>
    </AppShell>
  )
}

// A single tile card (header chrome + body).
function TileCard({
  tile,
  highlighted = false,
  spanClass,
  currentSpan,
  dragWidth,
  dragStartCol,
  customizing,
  locked = false,
  createdAfter,
  onExpand,
  onMoveUp,
  onMoveTop,
  onCycleWidth,
  onResizeStart,
  onRemove,
  onDrill,
}: {
  tile: TileDef
  spanClass: string
  currentSpan: number
  /** Live pixel width while this tile is being drag-resized; null otherwise. */
  dragWidth?: number | null
  /** The tile's anchor start column (1-based) during a drag-resize. */
  dragStartCol?: number | null
  customizing: boolean
  /** Pinned: no grip, no resize edge - the tile holds still and its text selects. */
  locked?: boolean
  createdAfter?: string | null
  onExpand: () => void
  onMoveUp: () => void
  onMoveTop: () => void
  onCycleWidth: () => void
  onResizeStart: (e: React.PointerEvent) => void
  onRemove: () => void
  onDrill: DrillHandler
  /** Briefly ring the card the user just added or put back, so it is found. */
  highlighted?: boolean
}) {
  // Generic live-data resolution: a tile with a `live` binding whose `source` is
  // a registered worklist source renders through the ONE generic LiveTable. The
  // source registry (live-sources.tsx) decides columns / query / gating - adding
  // a source is one registry entry, never a branch here. Feature flags are read
  // once as a map so a source's optional `requiresFlag` gate stays generic.
  const flags = useFeatureFlags()
  const liveSource = getLiveSource(tile.data_set?.source)
  // The tile's single header row hosts the body renderer's controls (chart/table
  // toggle, map/grid toggle, table toolbar) on the right, next to the frame's own
  // controls. The renderer portals them into this slot via <HeaderControls>.
  const [headerSlot, setHeaderSlot] = useState<HTMLDivElement | null>(null)
  return (
    <section
      data-tile-id={tile.key}
      data-tile-title={tile.title}
      className={cn(
        'cv-tile group relative col-span-1 rounded-[15px] border bg-[var(--bg-card)] p-5 transition-shadow duration-500',
        highlighted && 'ring-2 ring-[var(--green)] ring-offset-2 ring-offset-[var(--bg)]',
        customizing ? 'border-dashed border-[var(--rule-strong)]' : 'border-[var(--rule)]',
        // Drag-reorder highlight (dragging dims, drop target rings) lives in
        // index.css - unlayered so it beats the .cv-tile:hover shadow mid-drag.
        spanClass,
      )}
      // While drag-resizing, follow the pointer smoothly at a live pixel width
      // (dynamic runtime value - the sanctioned inline-style exception). The tile
      // is anchored at its current start column and reserves the rest of the row
      // (so it grows rightward from where it sits, never jumping to the edge),
      // rendered left-aligned at the live width. `transition:none` keeps it locked
      // to the cursor, and it sits above its neighbours as it expands. On drop
      // this clears and the snapped span class takes over.
      style={
        dragWidth
          ? {
              width: `${dragWidth}px`,
              gridColumn: `${dragStartCol ?? 1} / -1`,
              justifySelf: 'start',
              transition: 'none',
              zIndex: 20,
            }
          : undefined
      }
    >
      {/* Drag-to-resize: grab the right edge and pull to stretch the card
          across the columns (snaps to third / half / two-thirds / full).
          Appears on hover, desktop pointers only; skipped by the reorder. */}
      {!locked && (
        <div
          data-resize-handle
          onPointerDown={onResizeStart}
          title="Drag to resize"
          className="absolute right-0 top-1/2 z-20 hidden h-14 w-1.5 -translate-y-1/2 translate-x-1/2 cursor-col-resize rounded-full bg-[var(--rule-strong)] opacity-0 transition-opacity duration-150 hover:bg-[var(--green)] group-hover:opacity-100 md:block"
        />
      )}
      <div className="mb-3 flex items-center gap-2">
        {!locked && (
          <span
            className={cn(
              'inline-flex cursor-grab touch-none rounded-[6px] px-0.5 text-[var(--ink-3)] transition-colors hover:bg-[var(--green-bg)] hover:text-[var(--green)] active:cursor-grabbing',
              customizing ? 'opacity-90' : 'opacity-70',
            )}
            title="Drag to move this card"
          >
            <GripVertical size={15} />
          </span>
        )}
        <span className="flex-1 text-[11.5px] font-medium uppercase tracking-[0.12em] text-[var(--ink-2)]">
          {tile.title}
        </span>
        {/* Right side: the body renderer's controls portal in here (single row),
            followed by the frame's own controls. */}
        <div ref={setHeaderSlot} className="flex items-center gap-2 empty:hidden" />
        {customizing && (
          <>
            <Button
              variant="ghost"
              onClick={onMoveUp}
              aria-label="Move up"
              title="Move up"
              className="grid h-6 w-6 place-items-center rounded-[7px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-card)] p-0 text-[var(--ink-3)] hover:border-[var(--green-border)] hover:bg-[var(--bg-card)] hover:text-[var(--green)]"
            >
              <ArrowUp size={13} />
            </Button>
            <Button
              variant="ghost"
              onClick={onMoveTop}
              aria-label="Move to top"
              title="Move to top"
              className="grid h-6 w-6 place-items-center rounded-[7px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-card)] p-0 text-[var(--ink-3)] hover:border-[var(--green-border)] hover:bg-[var(--bg-card)] hover:text-[var(--green)]"
            >
              <ChevronsUp size={13} />
            </Button>
            <Button
              variant="ghost"
              onClick={onCycleWidth}
              aria-label="Change tile width"
              title="Resize (third / half / two-thirds / full)"
              className="grid h-6 w-6 place-items-center rounded-[7px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-card)] p-0 text-[11px] font-medium leading-none text-[var(--ink-3)] hover:border-[var(--green-border)] hover:bg-[var(--bg-card)] hover:text-[var(--green)]"
            >
              {{ 4: '⅓', 6: '½', 8: '⅔', 12: '1' }[currentSpan] ?? '½'}
            </Button>
          </>
        )}
        <Button
          variant="ghost"
          onClick={onExpand}
          aria-label="Expand tile"
          data-tour="dashboard-card-expand"
          title="Expand"
          className="grid h-6 w-6 place-items-center rounded-[7px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-card)] p-0 text-[var(--ink-3)] opacity-70 transition-opacity hover:border-[var(--green-border)] hover:bg-[var(--bg-card)] hover:text-[var(--green)] hover:opacity-100"
        >
          <Maximize2 size={13} />
        </Button>
        {customizing && (
          <Button
            variant="ghost"
            onClick={onRemove}
            aria-label="Remove tile"
            title="Remove tile"
            className="grid h-6 w-6 place-items-center rounded-[7px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-card)] p-0 text-[var(--ink-3)] hover:border-[var(--status-error)] hover:bg-[var(--bg-card)] hover:text-[var(--status-error)]"
          >
            <X size={13} />
          </Button>
        )}
      </div>
      <TileHeaderSlot.Provider value={headerSlot}>
        {liveSource ? (
          // A live worklist: the ONE generic table renders every source. When the
          // source is gated by a capability the tenant lacks, show a neutral note
          // instead. Keyed by source so the generic table's per-source data hook
          // stays stable for the mounted instance.
          liveSource.requiresFlag && flags[liveSource.requiresFlag] !== true ? (
            <p className="py-4 text-[13px] text-[var(--ink-3)]">
              {liveSource.label} isn&rsquo;t enabled for this workspace.
            </p>
          ) : (
            <LiveTable
              key={`${tile.key}:${tile.data_set?.source}`}
              source={tile.data_set!.source!}
              binding={tile.data_set!}
              tableId={`dashboard-tile-${tile.key}`}
            />
          )
        ) : (
          <TileBody tile={tile} onDrill={onDrill} createdAfter={createdAfter} />
        )}
      </TileHeaderSlot.Provider>
    </section>
  )
}

// Favourites - the user's pinned shortcuts, drawn from the tenant's favourites
// catalogue (config), pins persisting per user. Split into a hook + two pieces so
// the "Add a shortcut" control lives in the header toolbar while the pinned chips
// (when any) sit on their own compact row - no heading / description chrome.
interface FavouritesState {
  catalog: FavDef[]
  pinned: Record<string, boolean>
  pinnedItems: FavDef[]
  setPin: (key: string, val: boolean) => void
}

function useFavourites(
  rawCatalog: FavDef[],
  toast: (m: string, k?: 'saved' | 'celebrate') => void,
): FavouritesState {
  // Hide shortcuts whose surface the tenant doesn't have (flag off), so the
  // Shortcuts menu never bleeds a surface the tenant can't reach (e.g. a tenant
  // without Transfers / Inspections). A shortcut with no flag is always shown.
  // The flag says the tenant has the surface; `any_permission` says this actor
  // may open it, so both have to pass before a shortcut is offered.
  const flags = useFeatureFlags()
  const { can } = usePermissions()
  const catalog = useMemo(
    () =>
      rawCatalog.filter(
        (f) =>
          (!f.flag || flags[f.flag] === true) &&
          (!f.any_permission?.length || f.any_permission.some((p) => can(p))),
      ),
    [rawCatalog, flags, can],
  )
  const KEY = `raven:dashboard-favs:v2:${tenantSlug()}`
  const [pinned, setPinned] = useState<Record<string, boolean>>(() => {
    const base: Record<string, boolean> = {}
    catalog.forEach((f) => {
      base[f.key] = f.default_pinned
    })
    try {
      const raw = localStorage.getItem(KEY)
      if (raw) {
        const saved = JSON.parse(raw) as Record<string, boolean>
        catalog.forEach((f) => {
          if (f.key in saved) base[f.key] = saved[f.key]
        })
      }
    } catch {
      /* ignore - fall back to catalogue defaults */
    }
    return base
  })
  const setPin = (key: string, val: boolean) => {
    setPinned((prev) => {
      const next = { ...prev, [key]: val }
      try {
        localStorage.setItem(KEY, JSON.stringify(next))
      } catch {
        /* ignore - pins just don't persist */
      }
      return next
    })
    const label = catalog.find((f) => f.key === key)?.label ?? 'shortcut'
    toast(val ? `Added “${label}” to your shortcuts` : `Removed “${label}” from your shortcuts`)
  }
  return { catalog, pinned, pinnedItems: catalog.filter((f) => pinned[f.key]), setPin }
}

// The pinned shortcuts: each a button that navigates to its surface, sitting
// inline in the toolbar next to the Shortcuts control. Styled as the same header
// button as Shortcuts / Export (h-9, hairline, bg-card) with a diagonal arrow,
// since a shortcut is a link out. Removal happens in the "Shortcuts" menu (add +
// remove), so no dismiss control here. Renders nothing when there are no pins.
function FavouriteChips({ fav }: { fav: FavouritesState }) {
  const navigate = useNavigate()
  if (fav.pinnedItems.length === 0) return null
  return (
    <>
      {fav.pinnedItems.map((f) => (
        <Button
          key={f.key}
          variant="ghost"
          onClick={() => navigate(f.to)}
          className="group/sc flex h-9 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-[9px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-card)] px-3 text-[13px] font-normal text-[var(--ink)] transition-colors hover:border-[var(--rule-strong)] hover:bg-[var(--bg-subtle)]"
        >
          {f.label}
          <ArrowUpRight
            size={14}
            className="text-[var(--ink-3)] transition-opacity group-hover/sc:text-[var(--ink)]"
          />
        </Button>
      ))}
    </>
  )
}

// The "Shortcuts" control for the header toolbar: one surface for both adding and
// removing shortcuts. Each catalogue row toggles on/off (a check marks the ones
// pinned as capsules below). Styled to match the other header actions.
function AddShortcut({ fav }: { fav: FavouritesState }) {
  const [pickerOpen, setPickerOpen] = useState(false)
  return (
    <div className="relative">
      <Button
        variant="ghost"
        onClick={() => setPickerOpen((v) => !v)}
        className="flex h-9 items-center gap-2 rounded-[9px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-card)] px-3 text-[13px] font-normal text-[var(--ink)] transition-colors hover:border-[var(--rule-strong)] hover:bg-[var(--bg-subtle)]"
      >
        <Bookmark size={15} /> Shortcuts
      </Button>
      {pickerOpen && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setPickerOpen(false)} />
          <div className="absolute left-0 top-[calc(100%+8px)] z-40 min-w-[248px] rounded-[13px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-card)] p-1.5 shadow-[var(--shadow-float)]">
            <h4 className="mx-2.5 mb-0.5 mt-2 text-[10.5px] font-medium uppercase tracking-[0.15em] text-[var(--ink-3)]">
              Shortcuts
            </h4>
            <p className="mx-2.5 mb-1.5 text-[12px] text-[var(--ink-3)]">
              Pick the pages to keep one click away.
            </p>
            {fav.catalog.length === 0 && (
              <div className="px-3 py-2 text-[13px] text-[var(--ink-3)]">Nothing to add.</div>
            )}
            {fav.catalog.map((f) => {
              const on = !!fav.pinned[f.key]
              return (
                <Button
                  key={f.key}
                  variant="ghost"
                  aria-pressed={on}
                  onClick={() => fav.setPin(f.key, !on)}
                  className="flex h-auto w-full items-center justify-start gap-2.5 rounded-lg px-2.5 py-2 text-left text-[13px] font-normal text-[var(--ink)] transition-colors hover:bg-[var(--bg-subtle)]"
                >
                  <span className="w-[18px] text-center text-[var(--ink-3)]">{f.icon}</span>
                  <span className="flex-1">{f.label}</span>
                  <span
                    className={`grid h-[18px] w-[18px] place-items-center rounded-[5px] border-[0.5px] transition-colors ${
                      on
                        ? 'border-[var(--green)] bg-[var(--green)] text-white'
                        : 'border-[var(--rule)] text-transparent'
                    }`}
                  >
                    <Check size={12} strokeWidth={3} />
                  </span>
                </Button>
              )
            })}
          </div>
        </>
      )}
    </div>
  )
}

// Focused (expanded) tile - a tile popped into a large centred view. Works for
// any tile regardless of its grid span (unlike a span change, which does
// nothing for tiles that are already full width). Resolves live data the same
// way TileCard does. Contract (Minimise) / backdrop / Esc all close it.
function ExpandedTile({
  tile,
  createdAfter,
  onClose,
  onDrill,
}: {
  tile: TileDef
  createdAfter?: string | null
  onClose: () => void
  onDrill: DrillHandler
}) {
  const liveSource = getLiveSource(tile.data_set?.source)
  const [headerSlot, setHeaderSlot] = useState<HTMLDivElement | null>(null)
  // Lock the page scroll while the tile is expanded so a wheel/trackpad scroll
  // moves the tile's own content, not the dimmed dashboard behind it.
  useEffect(() => {
    const de = document.documentElement
    const prevHtml = de.style.overflow
    const prevBody = document.body.style.overflow
    de.style.overflow = 'hidden'
    document.body.style.overflow = 'hidden'
    return () => {
      de.style.overflow = prevHtml
      document.body.style.overflow = prevBody
    }
  }, [])
  // Portalled to <body> so it centres over the whole viewport regardless of any
  // transformed/filtered ancestor (which would otherwise make `fixed` relative
  // to that ancestor and offset the modal).
  // z-50 is the shared overlay layer (Dialog / Select / Popover). Sharing it
  // means anything opened from inside the tile - the Filters menu, a Select -
  // portals after this node and so stacks in front of it, and the row detail
  // drawer (z-[55]) opens over it too.
  return createPortal(
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-[rgba(20,18,14,0.55)] p-4 backdrop-blur-[2px] sm:p-8"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="flex max-h-[90vh] w-[96vw] max-w-[1500px] flex-col overflow-hidden rounded-[18px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-card)] shadow-[var(--shadow-float)]"
      >
        <div className="flex items-center gap-3 border-b-[0.5px] border-[var(--rule)] px-6 py-4">
          <span className="flex-1 text-[10.5px] font-medium uppercase tracking-[0.15em] text-[var(--ink-3)]">
            {tile.title}
          </span>
          <div ref={setHeaderSlot} className="flex items-center gap-2 empty:hidden" />
          <Button
            variant="ghost"
            onClick={onClose}
            aria-label="Contract tile"
            title="Contract"
            className="grid h-8 w-8 place-items-center rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-card)] p-0 text-[var(--ink-3)] transition-colors hover:border-[var(--green-border)] hover:bg-[var(--bg-card)] hover:text-[var(--green)]"
          >
            <Minimize2 size={15} />
          </Button>
        </div>
        <div className="overflow-auto overscroll-contain px-6 py-6">
          <TileHeaderSlot.Provider value={headerSlot}>
            {liveSource ? (
              <LiveTable
                key={`${tile.key}:${tile.data_set?.source}:expanded`}
                source={tile.data_set!.source!}
                binding={tile.data_set!}
                // The same table as the card: its filters and columns carry over.
                tableId={`dashboard-tile-${tile.key}`}
                manage
              />
            ) : (
              <TileBody tile={tile} onDrill={onDrill} createdAfter={createdAfter} manage />
            )}
          </TileHeaderSlot.Provider>
        </div>
      </div>
    </div>,
    document.body,
  )
}
